import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Checkbox, Form, Input, Modal, Pagination, Progress, Select, Space, Spin, Table, Tabs, Tag, Tooltip, Typography, message } from 'antd';
import { CopyOutlined, ExclamationCircleFilled, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import request from '../../utils/request';
import { auditWaybillFile, type WaybillAuditIssue } from '../../utils/waybillAudit';

const { Title, Text } = Typography;
const { Option } = Select;

interface BatchFilters {
  keyword?: string;
  template_system_code?: string;
  instruction_system_code?: string;
}

const statusLabelMap: Record<string, { text: string; color: string }> = {
  draft: { text: '草稿', color: 'default' },
  precheck_done: { text: '预审完成', color: 'gold' },
  executing: { text: '执行中', color: 'blue' },
  executed: { text: '执行完成', color: 'green' },
  cancelled: { text: '已取消', color: 'red' },
};

const DROPSHIP_TEMPLATE_CODE = 'InstructionTemplate-SYS-19';

const InstructionBatchListPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [filters, setFilters] = useState<BatchFilters>({});
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [searchInstrCode, setSearchInstrCode] = useState('');
  const [searchOnlyBatchId, setSearchOnlyBatchId] = useState<number | null>(null);
  const [otherRows, setOtherRows] = useState<any[]>([]);
  const [otherFilters, setOtherFilters] = useState<BatchFilters>({});
  const [otherForm] = Form.useForm();
  const [otherPagination, setOtherPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [otherLoading, setOtherLoading] = useState(false);
  const [carrierModalOpen, setCarrierModalOpen] = useState(false);
  const [carrierModalRows, setCarrierModalRows] = useState<Array<{ carrier: string; count: number }>>([]);
  const [carrierModalTitle, setCarrierModalTitle] = useState<string>('');
  const [inventoryModalOpen, setInventoryModalOpen] = useState(false);
  const [inventoryModalRows, setInventoryModalRows] = useState<any[]>([]);
  const [inventoryModalTitle, setInventoryModalTitle] = useState<string>('');
  const [precheckOpen, setPrecheckOpen] = useState(false);
  const [precheckLoading, setPrecheckLoading] = useState(false);
  const [precheckRows, setPrecheckRows] = useState<any[]>([]);
  const [precheckBatchId, setPrecheckBatchId] = useState<number | null>(null);
  const [precheckActive, setPrecheckActive] = useState('upload_immediate');
  const [precheckTitle, setPrecheckTitle] = useState('');
  const [precheckPage, setPrecheckPage] = useState(1);
  const [precheckPageSize, setPrecheckPageSize] = useState(10);
  const [precheckSelections, setPrecheckSelections] = useState<Record<string, { allSelected: boolean; excluded: Set<string>; included: Set<string> }>>({});
  const [precheckResultMap, setPrecheckResultMap] = useState<Record<string, 'pass' | 'reject' | undefined>>({});
  const [precheckRiskMap, setPrecheckRiskMap] = useState<Record<string, string[]>>({});
  const [precheckExecutionMap, setPrecheckExecutionMap] = useState<Record<string, string>>({});
  const [execReminderOpen, setExecReminderOpen] = useState(false);
  const [execReminderTargetIds, setExecReminderTargetIds] = useState<string[]>([]);
  const [execReminderTargetCodes, setExecReminderTargetCodes] = useState<string[]>([]);
  const [execReminderText, setExecReminderText] = useState('');
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTargetIds, setRejectTargetIds] = useState<string[]>([]);
  const [rejectTargetCodes, setRejectTargetCodes] = useState<string[]>([]);
  const [rejectReasons, setRejectReasons] = useState<string[]>([]);
  const [rejectOtherText, setRejectOtherText] = useState('');
  const [rejectReasonOptions, setRejectReasonOptions] = useState<Array<{ label: string; value: string }>>([
    { label: '物流信息重复', value: '物流信息重复' },
    { label: '跟踪号历史重复', value: '跟踪号历史重复' },
    { label: '运单审核失败', value: '运单审核失败' },
    { label: '其他', value: '其他' },
  ]);
  const [autoPrecheckRunning, setAutoPrecheckRunning] = useState(false);
  const [autoPrecheckOpen, setAutoPrecheckOpen] = useState(false);
  const [autoPrecheckMode, setAutoPrecheckMode] = useState<'upload_immediate' | 'warehouse' | 'upload_after_pack' | null>(null);
  const [autoPrecheckSummary, setAutoPrecheckSummary] = useState<{
    total: number;
    duplicatePairs: Array<{ carrier: string; tracking: string; count: number; serials: string[] }>;
    historyWarnings: Array<{ serial: string; items: Array<{ provider: string; tracking_number: string }> }>;
    auditIssues: Array<{ serial: string; fileName: string; label: string; message: string }>;
  }>({ total: 0, duplicatePairs: [], historyWarnings: [], auditIssues: [] });
  const [autoPrecheckWarehouseMismatches, setAutoPrecheckWarehouseMismatches] = useState<Array<{
    code: string;
    inputCity: string;
    inputState: string;
    inputZip: string;
    zipCity: string;
    zipState: string;
  }>>([]);
  const [autoPrecheckAfterPackWarnings, setAutoPrecheckAfterPackWarnings] = useState<{
    noPack: string[];
    specialNoRemark: string[];
  }>({ noPack: [], specialNoRemark: [] });
  const [batchTab, setBatchTab] = useState<'dropship' | 'outbound' | 'other'>('dropship');
  const [autoPrecheckProgress, setAutoPrecheckProgress] = useState({ total: 0, done: 0 });
  const [autoPrecheckDupPage, setAutoPrecheckDupPage] = useState(1);
  const [autoPrecheckDupPageSize, setAutoPrecheckDupPageSize] = useState(10);
  const [autoPrecheckHistoryPage, setAutoPrecheckHistoryPage] = useState(1);
  const [autoPrecheckHistoryPageSize, setAutoPrecheckHistoryPageSize] = useState(10);
  const [autoPrecheckAuditPage, setAutoPrecheckAuditPage] = useState(1);
  const [autoPrecheckAuditPageSize, setAutoPrecheckAuditPageSize] = useState(10);
  const autoPrecheckCancelRef = React.useRef(false);
  const fetchDataRef = React.useRef<() => void>(() => {});
  const [attPreviewOpen, setAttPreviewOpen] = useState(false);
  const [attPreviewLoading, setAttPreviewLoading] = useState(false);
  const [attPreviewTitle, setAttPreviewTitle] = useState('');
  const [attPreviewKind, setAttPreviewKind] = useState<'image' | 'pdf' | 'other'>('other');
  const [attPreviewUrl, setAttPreviewUrl] = useState('');
  const attPreviewObjectUrlRef = React.useRef<string | null>(null);
  const [completePrecheckLoading, setCompletePrecheckLoading] = useState(false);
  const [completePrecheckOpen, setCompletePrecheckOpen] = useState(false);
  const [completePrecheckProgress, setCompletePrecheckProgress] = useState({ total: 0, done: 0 });
  const [completePrecheckErrors, setCompletePrecheckErrors] = useState<string[]>([]);
  const [completePrecheckErrorsOpen, setCompletePrecheckErrorsOpen] = useState(false);
  const [finalReviewBatchId, setFinalReviewBatchId] = useState<number | null>(null);
  const getPrecheckRowQty = useCallback((row: any) => {
    try {
      const targets = Array.isArray(row?.targets_detail) ? row.targets_detail : [];
      const seen = new Set<string>();
      let total = 0;
      for (const t of targets) {
        const snap = t?.snapshot || {};
        const code = t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || t?.inventory_unit_id || '';
        const sort = (Array.isArray(t?.sample_item_barcodes) && t.sample_item_barcodes.length > 0)
          ? t.sample_item_barcodes[0]
          : (Array.isArray(snap?.sorting_barcodes) && snap.sorting_barcodes.length > 0 ? snap.sorting_barcodes[0]?.code : (snap?.sorting_barcode || ''));
        const qty = Number(t?.expected_qty || 0);
        const key = `${code}|${sort}|${qty}`;
        if (seen.has(key)) continue;
        seen.add(key);
        if (!isNaN(qty)) total += qty;
      }
      return total;
    } catch {
      return 0;
    }
  }, []);
  const getPrecheckFilteredRows = useCallback((rows: any[], activeKey: string) => {
    const list = Array.isArray(rows) ? rows : [];
    return list.filter((it) => {
      const source = String(it?.waybill_source || '').trim();
      const mode = String(it?.waybill_submit_mode || '').trim();
      if (activeKey === 'warehouse') {
        return source === 'warehouse';
      }
      if (activeKey === 'upload_after_pack') {
        return source === 'upload' && mode === 'after_pack';
      }
      return source === 'upload' && mode === 'immediate';
    });
  }, []);
  const precheckStats = useMemo(() => {
    const rows = Array.isArray(precheckRows) ? precheckRows : [];
    const totalOrders = rows.length;
    const totalQty = rows.reduce((sum, r) => sum + getPrecheckRowQty(r), 0);
    const uploadImmediateRows = getPrecheckFilteredRows(rows, 'upload_immediate');
    const warehouseRows = getPrecheckFilteredRows(rows, 'warehouse');
    const uploadAfterPackRows = getPrecheckFilteredRows(rows, 'upload_after_pack');
    const countOrders = (list: any[]) => list.length;
    const countQty = (list: any[]) => list.reduce((sum, r) => sum + getPrecheckRowQty(r), 0);
    return {
      totalOrders,
      totalQty,
      uploadImmediate: { orders: countOrders(uploadImmediateRows), qty: countQty(uploadImmediateRows) },
      warehouse: { orders: countOrders(warehouseRows), qty: countQty(warehouseRows) },
      uploadAfterPack: { orders: countOrders(uploadAfterPackRows), qty: countQty(uploadAfterPackRows) },
    };
  }, [precheckRows, getPrecheckRowQty, getPrecheckFilteredRows]);
  const precheckMarkStats = useMemo(() => {
    const rows = Array.isArray(precheckRows) ? precheckRows : [];
    let pass = 0;
    let reject = 0;
    let none = 0;
    rows.forEach((r) => {
      const id = String(r?.id ?? '');
      const mark = precheckResultMap[id];
      if (mark === 'pass') pass += 1;
      else if (mark === 'reject') reject += 1;
      else none += 1;
    });
    return { pass, reject, none };
  }, [precheckRows, precheckResultMap]);
  const precheckSortedRows = useMemo(() => {
    const list = getPrecheckFilteredRows(precheckRows, precheckActive);
    const priority = ['USPS', 'UPS', 'FEDEX'];
    const normalize = (v: any) => String(v || '').trim().toUpperCase();
    return [...list].sort((a, b) => {
      const ra = precheckRiskMap[String(a?.instruction_system_code || '')] || [];
      const rb = precheckRiskMap[String(b?.instruction_system_code || '')] || [];
      const aa = ra.includes('运单审核失败') ? 0 : 1;
      const bb = rb.includes('运单审核失败') ? 0 : 1;
      if (aa !== bb) return aa - bb;
      const ca = normalize(a?.waybill_carrier);
      const cb = normalize(b?.waybill_carrier);
      const ia = priority.indexOf(ca);
      const ib = priority.indexOf(cb);
      const pa = ia === -1 ? 999 : ia;
      const pb = ib === -1 ? 999 : ib;
      if (pa !== pb) return pa - pb;
      if (ca && cb && ca !== cb) return ca.localeCompare(cb);
      return 0;
    });
  }, [precheckRows, precheckActive, getPrecheckFilteredRows, precheckRiskMap]);
  const autoPrecheckFinished = autoPrecheckProgress.total > 0 && autoPrecheckProgress.done >= autoPrecheckProgress.total && !autoPrecheckRunning;
  const precheckCarrierColors = useMemo(() => ([
    '#f5f7ff', '#f3fbff', '#f6ffed', '#fff7e6', '#fff0f6',
    '#f9f0ff', '#f6ffed', '#e6fffb', '#fffbe6', '#f0f5ff',
  ]), []);
  const openExecReminder = useCallback((row: any) => {
    const id = String(row?.id ?? '');
    const code = String(row?.instruction_system_code || '');
    if (!id) return;
    setExecReminderTargetIds([id]);
    setExecReminderTargetCodes(code ? [code] : []);
    setExecReminderText(String(precheckExecutionMap[id] || ''));
    setExecReminderOpen(true);
  }, [precheckExecutionMap]);
  const openExecReminderForIds = useCallback((ids: string[], rows: any[]) => {
    const useIds = (ids || []).map((x) => String(x || '')).filter(Boolean);
    if (!useIds.length) return;
    const codeSet = new Set<string>();
    rows.forEach((r: any) => {
      const id = String(r?.id ?? '');
      if (useIds.includes(id)) {
        const code = String(r?.instruction_system_code || '');
        if (code) codeSet.add(code);
      }
    });
    setExecReminderTargetIds(useIds);
    setExecReminderTargetCodes(Array.from(codeSet));
    setExecReminderText('');
    setExecReminderOpen(true);
  }, []);
  const confirmExecReminder = useCallback(() => {
    if (!execReminderTargetIds.length) { setExecReminderOpen(false); return; }
    const text = String(execReminderText || '').trim();
    setPrecheckExecutionMap((prev) => {
      const next = { ...prev };
      execReminderTargetIds.forEach((id) => { next[id] = text; });
      return next;
    });
    setPrecheckResultMap((prev) => {
      const next = { ...prev };
      execReminderTargetIds.forEach((id) => { next[id] = 'pass'; });
      return next;
    });
    if (execReminderTargetCodes.length) {
      setPrecheckRiskMap((prev) => {
        const next = { ...prev };
        execReminderTargetCodes.forEach((code) => { delete next[code]; });
        return next;
      });
    }
    setExecReminderOpen(false);
  }, [execReminderTargetIds, execReminderTargetCodes, execReminderText]);
  const loadPrecheckRows = useCallback(async (batchId?: number | string) => {
    const bid = Number(batchId);
    if (!Number.isFinite(bid) || !bid) return;
    setPrecheckLoading(true);
    try {
      const resp = await request.get('/api/v1/instruction/batch-items/', {
        params: { batch: bid, page_size: 1000 },
      });
      const payload = resp?.data || {};
      const list = Array.isArray(payload?.results) ? payload.results : (Array.isArray(payload) ? payload : []);
      setPrecheckRows(list);
    } catch (e: any) {
      message.error(e?.message || '加载预审列表失败');
    } finally {
      setPrecheckLoading(false);
    }
  }, []);
  const handleCompletePrecheck = useCallback(async () => {
    if (completePrecheckLoading) return;
    const rows = Array.isArray(precheckRows) ? precheckRows : [];
    const passRows = rows.filter((r) => precheckResultMap[String(r?.id ?? '')] === 'pass');
    const rejectRows = rows.filter((r) => precheckResultMap[String(r?.id ?? '')] === 'reject');
    const noneRows = rows.filter((r) => !precheckResultMap[String(r?.id ?? '')]);
    if (noneRows.length > 0) {
      message.warning('仍有未标注的记录');
      return;
    }
    setCompletePrecheckLoading(true);
    setCompletePrecheckOpen(true);
    setCompletePrecheckProgress({ total: passRows.length + rejectRows.length, done: 0 });
    setCompletePrecheckErrors([]);
    const errors: string[] = [];
    const rejectedBatchItemIds: string[] = [];
    const runWithLimit = async (tasks: Array<() => Promise<void>>, limit: number) => {
      const pool: Promise<void>[] = [];
      let i = 0;
      const enqueue = async () => {
        if (i >= tasks.length) return;
        const task = tasks[i++];
        const p = task().finally(() => {
          const idx = pool.indexOf(p);
          if (idx >= 0) pool.splice(idx, 1);
          setCompletePrecheckProgress((prev) => ({ ...prev, done: Math.min(prev.done + 1, prev.total) }));
        });
        pool.push(p);
        if (pool.length >= limit) {
          await Promise.race(pool);
        }
        await enqueue();
      };
      await enqueue();
      await Promise.all(pool);
    };
    try {
      const tasks: Array<() => Promise<void>> = [];
      passRows.forEach((r) => {
        tasks.push(async () => {
          const instrId = Number(r?.instruction);
          if (!Number.isFinite(instrId) || !instrId) {
            errors.push(`指令ID无效: ${String(r?.instruction_system_code || r?.id || '-')}`);
            return;
          }
          try {
            const bid = String(r?.id ?? '');
            const tips = String(precheckExecutionMap[bid] || '').trim();
            await request.post(`/api/v1/instruction/instructions/${instrId}/review/`, {
              decision: 'accept',
              estimated_working_days: 0,
              execution_tips: tips || undefined,
            });
          } catch (e: any) {
            errors.push(`受理失败: ${String(r?.instruction_system_code || instrId)}`);
          }
        });
      });
      rejectRows.forEach((r) => {
        tasks.push(async () => {
          const instrId = Number(r?.instruction);
          if (!Number.isFinite(instrId) || !instrId) {
            errors.push(`指令ID无效: ${String(r?.instruction_system_code || r?.id || '-')}`);
            return;
          }
          const code = String(r?.instruction_system_code || '');
          const reason = (precheckRiskMap[code] || []).map((x) => String(x || '').trim()).filter(Boolean).join('; ');
          try {
            await request.post(`/api/v1/instruction/instructions/${instrId}/review/`, {
              decision: 'reject',
              reason: reason || undefined,
            });
            const batchItemId = String(r?.id ?? '');
            if (batchItemId) rejectedBatchItemIds.push(batchItemId);
          } catch (e: any) {
            errors.push(`驳回失败: ${String(r?.instruction_system_code || instrId)}`);
          }
        });
      });
      if (tasks.length > 0) {
        await runWithLimit(tasks, 5);
      }
      if (precheckBatchId && rejectedBatchItemIds.length > 0) {
        try {
          await request.post(`/api/v1/instruction/batches/${precheckBatchId}/remove-items/`, {
            batch_item_ids: rejectedBatchItemIds,
          });
        } catch (e: any) {
          errors.push('驳回解绑失败');
        }
      }
      if (precheckBatchId) {
        try {
          const acceptedInstructionIds = passRows
            .map((r: any) => Number(r?.instruction || 0))
            .filter((id: number) => Number.isFinite(id) && id > 0);
          const ensureResp = await request.post(`/api/v1/instruction/batches/${precheckBatchId}/ensure-workorders-for-precheck/`, {
            instruction_ids: acceptedInstructionIds,
          });
          const ensureData = ensureResp?.data || {};
          const ensureFailedRows = Array.isArray(ensureData?.failed_rows) ? ensureData.failed_rows : [];
          if (ensureFailedRows.length > 0) {
            ensureFailedRows.slice(0, 100).forEach((row: any) => {
              const code = String(row?.instruction_system_code || row?.instruction_id || '-');
              const reason = String(row?.reason || '未生成工单');
              errors.push(`工单补齐失败: ${code} (${reason})`);
            });
          }
        } catch (e: any) {
          const failedRows = Array.isArray(e?.response?.data?.failed_rows) ? e.response.data.failed_rows : [];
          if (failedRows.length > 0) {
            failedRows.slice(0, 100).forEach((row: any) => {
              const code = String(row?.instruction_system_code || row?.instruction_id || '-');
              const reason = String(row?.reason || '未生成工单');
              errors.push(`工单补齐失败: ${code} (${reason})`);
            });
          } else {
            errors.push(String(e?.response?.data?.message || e?.message || '工单补齐校验失败'));
          }
        }
      }
      if (errors.length > 0) {
        setCompletePrecheckErrors(errors);
        setCompletePrecheckErrorsOpen(true);
        message.error(`完成预审失败 ${errors.length} 条`);
      } else {
        message.success('完成预审成功');
        if (precheckBatchId) {
          try {
            await request.patch(`/api/v1/instruction/batches/${precheckBatchId}/`, { status: 'precheck_done' });
          } catch (e: any) {
            errors.push('批次状态更新失败');
          }
          try {
            await Promise.resolve(fetchDataRef.current?.());
          } catch {}
        }
        setPrecheckOpen(false);
        setActiveStatus('precheck_done');
        setPagination((prev) => ({ ...prev, current: 1 }));
      }
    } finally {
      setCompletePrecheckLoading(false);
      setCompletePrecheckOpen(false);
    }
  }, [
    completePrecheckLoading,
    precheckRows,
    precheckResultMap,
    precheckExecutionMap,
    precheckRiskMap,
    precheckBatchId,
  ]);
  const getCarrierBg = useCallback((carrier: any) => {
    const v = String(carrier || 'UNKNOWN').trim().toUpperCase();
    let hash = 0;
    for (let i = 0; i < v.length; i += 1) {
      hash = ((hash << 5) - hash) + v.charCodeAt(i);
      hash |= 0;
    }
    const idx = Math.abs(hash) % precheckCarrierColors.length;
    return precheckCarrierColors[idx];
  }, [precheckCarrierColors]);
  useEffect(() => {
    setPrecheckPage(1);
  }, [precheckActive, precheckRows]);
  useEffect(() => {
    return () => {
      if (attPreviewObjectUrlRef.current) {
        try { URL.revokeObjectURL(attPreviewObjectUrlRef.current); } catch {}
        attPreviewObjectUrlRef.current = null;
      }
    };
  }, []);
  const resolveAttachmentUrl = useCallback((att: any): string | undefined => {
    try {
      const raw = att?.file || att?.file_path || att?.file_name || '';
      if (!raw) return undefined;
      const isAbs = /^https?:\/\//i.test(raw);
      let backendOrigin = window.location.origin;
      try {
        const u = new URL(backendOrigin);
        if (u.port === '3000') { u.port = '8000'; backendOrigin = u.toString().replace(/\/$/, ''); }
      } catch {}
      try {
        const anyReq: any = request as any;
        const baseURL = anyReq?.defaults?.baseURL || anyReq?.getUri?.();
        if (baseURL && /^https?:\/\//i.test(baseURL)) {
          const b = new URL(baseURL);
          backendOrigin = `${b.protocol}//${b.host}`;
        }
      } catch {}
      let path = raw;
      if (!isAbs) {
        if (!path.startsWith('/')) path = '/' + path;
        if (/^\/(instruction|attachments?)/i.test(path)) path = '/media' + path; else if (!/^\/media\//i.test(path)) path = '/media' + path;
      }
      return isAbs ? raw : `${backendOrigin}${path}`;
    } catch {
      return undefined;
    }
  }, []);
  const getExtLower = useCallback((att: any): string => {
    try {
      const raw = String(att?.file_name || att?.file || att?.file_path || '');
      const name = raw.split('?')[0].split('#')[0];
      const base = name.split('/').pop() || name;
      const ext = base.includes('.') ? base.split('.').pop() || '' : '';
      return String(ext).trim().toLowerCase();
    } catch {
      return '';
    }
  }, []);
  const getDisplayName = useCallback((att: any): string => {
    return String(att?.file_name || att?.file || att?.file_path || att?.label || '').trim();
  }, []);
  const openAttachmentPreview = useCallback(async (att: any) => {
    const url = resolveAttachmentUrl(att);
    if (!url) { message.warning('无可用文件'); return; }
    const extLower = getExtLower(att);
    const isImg = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extLower);
    const isPdf = extLower === 'pdf';
    const name = getDisplayName(att) || '附件预览';
    if (attPreviewObjectUrlRef.current) {
      try { URL.revokeObjectURL(attPreviewObjectUrlRef.current); } catch {}
      attPreviewObjectUrlRef.current = null;
    }
    setAttPreviewTitle(name);
    if (!isImg && !isPdf) {
      window.open(url, '_blank', 'noopener');
      return;
    }
    setAttPreviewKind(isImg ? 'image' : 'pdf');
    setAttPreviewOpen(true);
    setAttPreviewLoading(true);
    try {
      const resp = await fetch(url, { credentials: 'include' as any });
      if (!resp.ok) throw new Error('load failed');
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      attPreviewObjectUrlRef.current = objectUrl;
      setAttPreviewUrl(objectUrl);
    } catch {
      message.error('预览加载失败');
      setAttPreviewOpen(false);
    } finally {
      setAttPreviewLoading(false);
    }
  }, [resolveAttachmentUrl, getExtLower, getDisplayName]);
  const normalizeCarrier = useCallback((v: any) => String(v || '').trim().toLowerCase(), []);
  const normalizeTracking = useCallback((v: any) => String(v || '').replace(/\s+/g, '').trim().toUpperCase(), []);
  const getWaybillAttachments = useCallback((row: any) => {
    const list = Array.isArray(row?.attachments_detail) ? row.attachments_detail : [];
    const filtered = list.filter((a: any) => {
      const label = String(a?.label || '').toLowerCase();
      return label.includes('运单') || label.includes('waybill') || label.includes('tracking');
    });
    return filtered.length ? filtered : list;
  }, []);
  const runAutoPrecheckUploadImmediate = useCallback(async () => {
    if (autoPrecheckRunning) return;
    const rows = getPrecheckFilteredRows(precheckRows, 'upload_immediate');
    const excludeInstructionIds = Array.from(new Set(
      rows.map((r: any) => r?.instruction).filter((v: any) => v != null)
    ));
    setAutoPrecheckRunning(true);
    setAutoPrecheckMode('upload_immediate');
    setAutoPrecheckSummary({ total: rows.length, duplicatePairs: [], historyWarnings: [], auditIssues: [] });
    setAutoPrecheckWarehouseMismatches([]);
    setAutoPrecheckProgress({ total: 0, done: 0 });
    setAutoPrecheckOpen(true);
    autoPrecheckCancelRef.current = false;
    try {
      const pairCountMap = new Map<string, { carrier: string; tracking: string; count: number }>();
      const pairCodesMap = new Map<string, Set<string>>();
      rows.forEach((r) => {
        const carrierRaw = String(r?.waybill_carrier || '').trim();
        const trackingRaw = String(r?.waybill_tracking_no || '').trim();
        if (!carrierRaw || !trackingRaw) return;
        const key = `${normalizeCarrier(carrierRaw)}||${normalizeTracking(trackingRaw)}`;
        const existing = pairCountMap.get(key) || { carrier: carrierRaw, tracking: trackingRaw, count: 0 };
        existing.count += 1;
        pairCountMap.set(key, existing);
        const code = String(r?.instruction_system_code || '').trim();
        if (code) {
          const set = pairCodesMap.get(key) || new Set<string>();
          set.add(code);
          pairCodesMap.set(key, set);
        }
      });
      const duplicatePairs = Array.from(pairCountMap.entries())
        .map(([key, val]) => ({
          carrier: val.carrier,
          tracking: val.tracking,
          count: val.count,
          serials: Array.from(pairCodesMap.get(key) || []),
        }))
        .filter((it) => it.count > 1);

      const pairList = Array.from(pairCountMap.entries()).map(([key, val]) => ({
        key,
        carrier: val.carrier,
        tracking: val.tracking,
        codes: Array.from(pairCodesMap.get(key) || []),
      }));
      const precheckResults = await Promise.all(
        pairList.map(async (entry) => {
          try {
            const resp = await request.post('/api/v1/instruction/instructions/precheck/', {
              waybill_carrier: entry.carrier,
              waybill_tracking_no: entry.tracking,
              exclude_instruction_ids: excludeInstructionIds,
            });
            const warnings = Array.isArray((resp as any)?.data?.warnings)
              ? (resp as any).data.warnings
              : (Array.isArray((resp as any)?.warnings) ? (resp as any).warnings : []);
            return { entry, warnings };
          } catch {
            return { entry, warnings: [] };
          }
        })
      );
      const historyMap = new Map<string, Array<{ provider: string; tracking_number: string }>>();
      precheckResults.forEach(({ entry, warnings }) => {
        if (!warnings || warnings.length === 0) return;
        const items = warnings.map((w: any) => ({
          provider: String(w?.provider || entry.carrier || '').trim(),
          tracking_number: String(w?.tracking_number || entry.tracking || '').trim(),
        }));
        (entry.codes || []).forEach((code) => {
          if (!code) return;
          const existed = historyMap.get(code) || [];
          historyMap.set(code, [...existed, ...items]);
        });
      });
      const historyWarnings = Array.from(historyMap.entries()).map(([serial, items]) => ({
        serial,
        items: Array.from(new Map(items.map((it) => [`${it.provider}||${it.tracking_number}`, it])).values()),
      }));
      const riskMap: Record<string, string[]> = {};
      duplicatePairs.forEach((it) => {
        (it.serials || []).forEach((s) => {
          const key = String(s || '');
          if (!key) return;
          riskMap[key] = Array.from(new Set([...(riskMap[key] || []), '重复物流信息']));
        });
      });
      historyWarnings.forEach((it) => {
        const key = String(it.serial || '');
        if (!key) return;
        riskMap[key] = Array.from(new Set([...(riskMap[key] || []), '历史重复']));
      });

      const auditIssues: Array<{ serial: string; fileName: string; label: string; message: string }> = [];
      const auditTasks: Array<() => Promise<void>> = [];
      rows.forEach((row) => {
        const tracking = String(row?.waybill_tracking_no || '').trim();
        const serial = String(row?.instruction_system_code || '').trim();
        const attachments = getWaybillAttachments(row);
        attachments.forEach((att) => {
          const url = resolveAttachmentUrl(att);
          if (!url) return;
          auditTasks.push(async () => {
            if (autoPrecheckCancelRef.current) return;
            try {
              const resp = await fetch(url, { credentials: 'include' as any });
              if (!resp.ok) throw new Error('load failed');
              const blob = await resp.blob();
              const name = getDisplayName(att) || '附件';
              const file = new File([blob], name, { type: blob.type || 'application/octet-stream' });
              const uploadFile: any = { uid: `${serial}-${name}-${Date.now()}`, name, originFileObj: file };
              if (autoPrecheckCancelRef.current) return;
              const audit = await auditWaybillFile(uploadFile, tracking);
              if (autoPrecheckCancelRef.current) return;
              if (!audit.ok) {
                (audit.issues || []).forEach((issue: WaybillAuditIssue) => {
                  auditIssues.push({
                    serial: serial || '-',
                    fileName: audit.fileName || name,
                    label: issue.label || '',
                    message: issue.message || '',
                  });
                });
              }
            } catch (e: any) {
              if (autoPrecheckCancelRef.current) return;
              auditIssues.push({
                serial: serial || '-',
                fileName: getDisplayName(att) || '附件',
                label: '解析失败',
                message: `运单解析失败：${String(e?.message || e || '未知错误')}`,
              });
            } finally {
              if (!autoPrecheckCancelRef.current) {
                setAutoPrecheckProgress((prev) => ({ ...prev, done: Math.min(prev.done + 1, prev.total) }));
              }
            }
          });
        });
      });
      setAutoPrecheckProgress({ total: auditTasks.length, done: 0 });
      const runWithLimit = async (tasks: Array<() => Promise<void>>, limit: number) => {
        const pool: Promise<void>[] = [];
        let i = 0;
        const enqueue = async () => {
          if (autoPrecheckCancelRef.current) return;
          if (i >= tasks.length) return;
          const task = tasks[i++];
          const p = task().finally(() => {
            const idx = pool.indexOf(p);
            if (idx >= 0) pool.splice(idx, 1);
          });
          pool.push(p);
          if (pool.length >= limit) {
            await Promise.race(pool);
          }
          await enqueue();
        };
        await enqueue();
        await Promise.all(pool);
      };
      if (auditTasks.length > 0) {
        await runWithLimit(auditTasks, 5);
      }
      if (autoPrecheckCancelRef.current) return;
      auditIssues.forEach((it) => {
        const key = String(it.serial || '');
        if (!key) return;
        riskMap[key] = Array.from(new Set([...(riskMap[key] || []), '运单审核失败']));
      });

      setAutoPrecheckSummary({
        total: rows.length,
        duplicatePairs,
        historyWarnings,
        auditIssues,
      });
      setPrecheckRiskMap(riskMap);
      setAutoPrecheckOpen(true);
    } finally {
      setAutoPrecheckRunning(false);
    }
  }, [
    autoPrecheckRunning,
    getPrecheckFilteredRows,
    precheckRows,
    normalizeCarrier,
    normalizeTracking,
    getWaybillAttachments,
    resolveAttachmentUrl,
    getDisplayName,
  ]);
  const runAutoPrecheckWarehouse = useCallback(async () => {
    const rows = getPrecheckFilteredRows(precheckRows, 'warehouse');
    const normalize = (v: any) => String(v || '').trim().toLowerCase();
    const toUpper = (v: any) => String(v || '').trim().toUpperCase();
    setAutoPrecheckRunning(true);
    setAutoPrecheckMode('warehouse');
    setAutoPrecheckSummary({ total: rows.length, duplicatePairs: [], historyWarnings: [], auditIssues: [] });
    setAutoPrecheckWarehouseMismatches([]);
    setAutoPrecheckProgress({ total: rows.length, done: 0 });
    setAutoPrecheckOpen(true);
    autoPrecheckCancelRef.current = false;
    const mismatches: Array<{
      code: string;
      inputCity: string;
      inputState: string;
      inputZip: string;
      zipCity: string;
      zipState: string;
    }> = [];
    await Promise.all(rows.map(async (r: any) => {
      if (autoPrecheckCancelRef.current) return;
      const answers = r?.instruction_customer_answers || r?.customer_answers || {};
      const city = String((answers as any)?.city || '').trim();
      const state = String((answers as any)?.state || (answers as any)?.province || '').trim();
      const zip = String((answers as any)?.postal_code || (answers as any)?.zip || (answers as any)?.zipcode || '').trim();
      if (!zip) {
        if (!autoPrecheckCancelRef.current) {
          setAutoPrecheckProgress((prev) => ({ ...prev, done: Math.min(prev.done + 1, prev.total) }));
        }
        return;
      }
      try {
        const resp = await request.post('/api/v1/instruction/instructions/address-parse/', { text: zip });
        const parsed = resp?.data?.parsed || {};
        const zipCity = String(parsed?.zip_lookup_city || '').trim();
        const zipState = String(parsed?.zip_lookup_state || '').trim();
        if (!zipCity && !zipState) return;
        const mismatch = (zipCity && normalize(zipCity) !== normalize(city))
          || (zipState && normalize(zipState) !== normalize(state));
        if (mismatch) {
          mismatches.push({
            code: String(r?.instruction_system_code || '').trim(),
            inputCity: city,
            inputState: state,
            inputZip: zip,
            zipCity,
            zipState,
          });
        }
      } catch {
        // ignore parse errors
      } finally {
        if (!autoPrecheckCancelRef.current) {
          setAutoPrecheckProgress((prev) => ({ ...prev, done: Math.min(prev.done + 1, prev.total) }));
        }
      }
    }));
    if (autoPrecheckCancelRef.current) return;
    setAutoPrecheckWarehouseMismatches(mismatches);
    setAutoPrecheckRunning(false);
  }, [getPrecheckFilteredRows, precheckRows]);
  const runAutoPrecheckAfterPack = useCallback(() => {
    const rows = getPrecheckFilteredRows(precheckRows, 'upload_after_pack');
    setAutoPrecheckRunning(true);
    setAutoPrecheckMode('upload_after_pack');
    setAutoPrecheckSummary({ total: rows.length, duplicatePairs: [], historyWarnings: [], auditIssues: [] });
    setAutoPrecheckWarehouseMismatches([]);
    setAutoPrecheckAfterPackWarnings({ noPack: [], specialNoRemark: [] });
    setAutoPrecheckProgress({ total: rows.length, done: 0 });
    setAutoPrecheckOpen(true);
    autoPrecheckCancelRef.current = false;
    const noPack: string[] = [];
    const specialNoRemark: string[] = [];
    rows.forEach((r: any, idx: number) => {
      if (autoPrecheckCancelRef.current) return;
      const code = String(r?.instruction_system_code || '').trim();
      if (r?.is_pack_required === false) {
        noPack.push(code || '-');
      }
      const method = String(r?.estimated_packing_method || '').trim();
      const remark = String(r?.instruction_remarks || '').trim();
      if (method === 'special_packing' && !remark) {
        specialNoRemark.push(code || '-');
      }
      setAutoPrecheckProgress((prev) => ({ ...prev, done: Math.min(prev.done + 1, prev.total) }));
    });
    if (autoPrecheckCancelRef.current) return;
    setAutoPrecheckAfterPackWarnings({ noPack, specialNoRemark });
    setAutoPrecheckRunning(false);
  }, [getPrecheckFilteredRows, precheckRows]);
  const applyAllRisksAndClose = useCallback(() => {
    const codeToId = new Map<string, string>();
    const scopedRows = getPrecheckFilteredRows(precheckRows, 'upload_immediate');
    scopedRows.forEach((r: any) => {
      const code = String(r?.instruction_system_code || '');
      const id = String(r?.id ?? '');
      if (code && id) codeToId.set(code, id);
    });
    const duplicateCodes = new Set<string>();
    autoPrecheckSummary.duplicatePairs.forEach((it) => {
      (it.serials || []).forEach((s) => s && duplicateCodes.add(String(s)));
    });
    const historyCodes = new Set<string>();
    autoPrecheckSummary.historyWarnings.forEach((it) => {
      if (it.serial) historyCodes.add(String(it.serial));
    });
    const auditCodes = new Set<string>();
    autoPrecheckSummary.auditIssues.forEach((it) => {
      if (it.serial) auditCodes.add(String(it.serial));
    });
    const affectedCodes = new Set<string>([
      ...Array.from(duplicateCodes),
      ...Array.from(historyCodes),
      ...Array.from(auditCodes),
    ]);
    const rejectIds: string[] = [];
    affectedCodes.forEach((code) => {
      const id = codeToId.get(code);
      if (id) rejectIds.push(id);
    });
    setPrecheckResultMap((prev) => {
      const next = { ...prev };
      const rejectSet = new Set(rejectIds);
      codeToId.forEach((id) => {
        if (rejectSet.has(id)) next[id] = 'reject';
        else next[id] = 'pass';
      });
      return next;
    });
    setPrecheckRiskMap((prev) => {
      const next = { ...prev };
      affectedCodes.forEach((code) => {
        delete next[code];
      });
      duplicateCodes.forEach((code) => {
        next[code] = Array.from(new Set([...(next[code] || []), '物流信息重复']));
      });
      historyCodes.forEach((code) => {
        next[code] = Array.from(new Set([...(next[code] || []), '跟踪号历史重复']));
      });
      auditCodes.forEach((code) => {
        next[code] = Array.from(new Set([...(next[code] || []), '运单审核失败']));
      });
      return next;
    });
    setAutoPrecheckOpen(false);
  }, [autoPrecheckSummary, precheckRows, getPrecheckFilteredRows]);
  const applyAllRisksAndCloseWarehouse = useCallback(() => {
    const scopedRows = getPrecheckFilteredRows(precheckRows, 'warehouse');
    const codeToId = new Map<string, string>();
    scopedRows.forEach((r: any) => {
      const code = String(r?.instruction_system_code || '');
      const id = String(r?.id ?? '');
      if (code && id) codeToId.set(code, id);
    });
    const mismatchCodes = new Set<string>();
    autoPrecheckWarehouseMismatches.forEach((m) => {
      if (m.code) mismatchCodes.add(String(m.code));
    });
    const rejectIds: string[] = [];
    mismatchCodes.forEach((code) => {
      const id = codeToId.get(code);
      if (id) rejectIds.push(id);
    });
    setPrecheckResultMap((prev) => {
      const next = { ...prev };
      const rejectSet = new Set(rejectIds);
      codeToId.forEach((id) => {
        if (rejectSet.has(id)) next[id] = 'reject';
        else next[id] = 'pass';
      });
      return next;
    });
    setPrecheckRiskMap((prev) => {
      const next = { ...prev };
      codeToId.forEach((_id, code) => {
        delete next[code];
      });
      mismatchCodes.forEach((code) => {
        next[code] = ['邮编与地址不匹配'];
      });
      return next;
    });
    setAutoPrecheckOpen(false);
  }, [autoPrecheckWarehouseMismatches, precheckRows, getPrecheckFilteredRows]);
  const applyAllRisksAndCloseAfterPack = useCallback(() => {
    const scopedRows = getPrecheckFilteredRows(precheckRows, 'upload_after_pack');
    const codeToId = new Map<string, string>();
    scopedRows.forEach((r: any) => {
      const code = String(r?.instruction_system_code || '');
      const id = String(r?.id ?? '');
      if (code && id) codeToId.set(code, id);
    });
    const noPackCodes = new Set<string>(autoPrecheckAfterPackWarnings.noPack.filter(Boolean));
    const specialNoRemarkCodes = new Set<string>(autoPrecheckAfterPackWarnings.specialNoRemark.filter(Boolean));
    const affectedCodes = new Set<string>([...Array.from(noPackCodes), ...Array.from(specialNoRemarkCodes)]);
    const rejectIds: string[] = [];
    affectedCodes.forEach((code) => {
      const id = codeToId.get(code);
      if (id) rejectIds.push(id);
    });
    setPrecheckResultMap((prev) => {
      const next = { ...prev };
      const rejectSet = new Set(rejectIds);
      codeToId.forEach((id) => {
        if (rejectSet.has(id)) next[id] = 'reject';
        else next[id] = 'pass';
      });
      return next;
    });
    setPrecheckRiskMap((prev) => {
      const next = { ...prev };
      codeToId.forEach((_id, code) => {
        delete next[code];
      });
      noPackCodes.forEach((code) => {
        next[code] = Array.from(new Set([...(next[code] || []), '无需打包']));
      });
      specialNoRemarkCodes.forEach((code) => {
        next[code] = Array.from(new Set([...(next[code] || []), '未备注特殊包装具体要求']));
      });
      return next;
    });
    setAutoPrecheckOpen(false);
  }, [autoPrecheckAfterPackWarnings, precheckRows, getPrecheckFilteredRows]);
  const getSelectionState = useCallback((key: string) => {
    return precheckSelections[key] || { allSelected: false, excluded: new Set<string>(), included: new Set<string>() };
  }, [precheckSelections]);
  const getSelectedKeysForTab = useCallback((tabKey: string, rows: any[]) => {
    const state = getSelectionState(tabKey);
    const ids = rows.map((r) => String(r?.id ?? ''));
    if (state.allSelected) {
      return ids.filter((id) => id && !state.excluded.has(id));
    }
    return ids.filter((id) => id && state.included.has(id));
  }, [getSelectionState]);
  const statusTabs = useMemo(() => Object.entries(statusLabelMap).map(([key, item]) => ({
    key,
    label: item.text,
  })), []);
  const emptyStatusCounts = useMemo(() => (
    Object.keys(statusLabelMap).reduce((acc, k) => ({ ...acc, [k]: 0 }), {} as Record<string, number>)
  ), []);
  const [activeStatus, setActiveStatus] = useState<string>(statusTabs[0]?.key || 'draft');
  const [otherActiveStatus, setOtherActiveStatus] = useState<string>(statusTabs[0]?.key || 'draft');
  const [dropshipStatusCounts, setDropshipStatusCounts] = useState<Record<string, number>>({});
  const [outboundStatusCounts, setOutboundStatusCounts] = useState<Record<string, number>>({});
  const [otherStatusCounts, setOtherStatusCounts] = useState<Record<string, number>>({});
  const [mainTabCounts, setMainTabCounts] = useState<Record<'dropship' | 'outbound' | 'other', number>>({
    dropship: 0,
    outbound: 0,
    other: 0,
  });

  const tallyStatusCounts = useCallback((list: any[]) => {
    const base = { ...emptyStatusCounts };
    (Array.isArray(list) ? list : []).forEach((r: any) => {
      const st = String(r?.status || '').trim();
      if (Object.prototype.hasOwnProperty.call(base, st)) {
        base[st] = Number(base[st] || 0) + 1;
      }
    });
    return base;
  }, [emptyStatusCounts]);
  const loadAllBatches = useCallback(async (params: any) => {
    const pageSize = 200;
    const merged: any[] = [];
    let page = 1;
    let total = 0;
    for (let guard = 0; guard < 200; guard += 1) {
      const resp = await request.get('/api/v1/instruction/batches/', {
        params: { ...(params || {}), page, page_size: pageSize },
      });
      const payload = resp?.data || {};
      const list = Array.isArray(payload?.results) ? payload.results : (Array.isArray(payload) ? payload : []);
      const hasPagedShape = Array.isArray(payload?.results);
      if (page === 1) {
        const c = Number(payload?.count);
        total = Number.isFinite(c) && c >= 0 ? c : list.length;
      }
      merged.push(...(Array.isArray(list) ? list : []));
      if (!hasPagedShape) break;
      if (!list.length) break;
      if (total > 0 && page * pageSize >= total) break;
      page += 1;
    }
    return merged;
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
      };
      if (filters.keyword) params.search = filters.keyword;
      if (searchOnlyBatchId) params.id = searchOnlyBatchId;
      if (activeStatus) params.status = activeStatus;
      if (batchTab === 'dropship') {
        params.template_system_code = DROPSHIP_TEMPLATE_CODE;
      } else if (filters.template_system_code) {
        params.template_system_code = filters.template_system_code;
      }
      const fullList = await loadAllBatches(params);
      let results = Array.isArray(fullList) ? fullList : [];
      if (batchTab === 'dropship') {
        results = results.filter((r: any) => String(r?.template_system_code || '') === DROPSHIP_TEMPLATE_CODE);
      } else if (batchTab === 'outbound' || batchTab === 'other') {
        results = results.filter((r: any) => String(r?.template_system_code || '') !== DROPSHIP_TEMPLATE_CODE);
      }
      if (searchOnlyBatchId) {
        results = results.filter((r: any) => Number(r?.id) === Number(searchOnlyBatchId));
      }
      const count = results.length || 0;
      const pageSize = Number(pagination.pageSize || 20) || 20;
      const maxPage = Math.max(1, Math.ceil(count / pageSize));
      const safeCurrent = Math.min(Number(pagination.current || 1) || 1, maxPage);
      const start = (safeCurrent - 1) * pageSize;
      const paged = results.slice(start, start + pageSize);
      setRows(paged);
      setPagination((prev) => ({ ...prev, current: safeCurrent, total: count }));
    } catch (e: any) {
      message.error(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [activeStatus, batchTab, filters.keyword, filters.template_system_code, loadAllBatches, pagination.current, pagination.pageSize, searchOnlyBatchId]);

  const handleBatchFinalReview = useCallback(async (batchRow: any) => {
    const batchId = Number(batchRow?.id || 0);
    if (!(batchId > 0)) {
      message.error('批次ID无效');
      return;
    }
    if (finalReviewBatchId) return;
    let detail: any = null;
    try {
      const resp = await request.get(`/api/v1/instruction/batches/${batchId}/`);
      detail = resp?.data || null;
    } catch (e: any) {
      message.error(e?.message || '加载批次详情失败');
      return;
    }
    const rawItems = Array.isArray(detail?.items) ? detail.items : [];
    const rows = rawItems.map((it: any) => {
      const instructionId = Number(
        it?.instruction
        ?? it?.instruction_id
        ?? it?.instructionId
        ?? 0
      ) || 0;
      const instructionCode = String(it?.instruction_system_code || '').trim();
      const instructionStatus = String(it?.instruction_status || it?.status || '').trim().toLowerCase();
      const batchItemStatus = String(it?.status || '').trim().toLowerCase();
      return { instructionId, instructionCode, instructionStatus, batchItemStatus };
    }).filter((x: any) => x.instructionId > 0);
    const eligible = rows.filter((x: any) => x.instructionStatus === 'operation_done' && x.batchItemStatus !== 'excluded');
    const completedRows = rows.filter((x: any) => x.instructionStatus === 'completed');
    const otherSkippedRows = rows.filter((x: any) => (
      x.instructionStatus !== 'operation_done'
      && x.instructionStatus !== 'completed'
    ) || x.batchItemStatus === 'excluded');
    const alreadyCompleted = completedRows.length;
    const skipped = otherSkippedRows.length;
    if (eligible.length === 0) {
      message.warning('该批次无可终审指令（需处于“执行阶段完成(待终审)”）');
      return;
    }
    const confirmed = await new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: '确认批量终审（按指令）',
        okText: '开始终审',
        cancelText: '取消',
        content: (
          <div style={{ lineHeight: 1.8 }}>
            <div>{`批次：${String(detail?.system_code || batchRow?.system_code || `#${batchId}`)}`}</div>
            <div>{`可终审：${eligible.length} 条`}</div>
            <div>{`已完成将跳过：${alreadyCompleted} 条`}</div>
            {completedRows.length > 0 ? (
              <div style={{ marginLeft: 12, color: '#595959' }}>
                {completedRows.slice(0, 20).map((r: any, idx: number) => (
                  <div key={`done-skip-${r.instructionId}-${idx}`}>
                    {`${idx + 1}. ${r.instructionCode || `#${r.instructionId}`}`}
                  </div>
                ))}
                {completedRows.length > 20 ? <div>{`...其余 ${completedRows.length - 20} 条`}</div> : null}
              </div>
            ) : null}
            <div>{`其他状态将跳过：${skipped} 条`}</div>
            {otherSkippedRows.length > 0 ? (
              <div style={{ marginLeft: 12, color: '#595959' }}>
                {otherSkippedRows.slice(0, 20).map((r: any, idx: number) => (
                  <div key={`other-skip-${r.instructionId}-${idx}`}>
                    {`${idx + 1}. ${r.instructionCode || `#${r.instructionId}`}（状态:${r.instructionStatus || '-'}，批次状态:${r.batchItemStatus || '-'}）`}
                  </div>
                ))}
                {otherSkippedRows.length > 20 ? <div>{`...其余 ${otherSkippedRows.length - 20} 条`}</div> : null}
              </div>
            ) : null}
          </div>
        ),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;
    setFinalReviewBatchId(batchId);
    const okCodes: string[] = [];
    const noBillingRows: Array<{ code: string; reason: string }> = [];
    const failRows: Array<{ code: string; reason: string }> = [];
    const finalizePolicyRows: Array<{ code: string; mode: string; source: string; matched: string; fallbackReason: string }> = [];
    let ensureSummary: any = null;
    try {
      try {
        const ensureResp = await request.post(`/api/v1/instruction/batches/${batchId}/ensure-workorders-for-final-review/`, {});
        ensureSummary = ensureResp?.data || null;
      } catch (e: any) {
        const reason = String(e?.response?.data?.detail || e?.response?.data?.message || e?.message || '工单补齐失败');
        Modal.error({
          title: '批量终审前检查失败',
          content: `无法完成“工单补齐”预处理：${reason}`,
        });
        return;
      }
      const ensureFailedMap: Record<number, string> = {};
      const readySet = new Set<number>(
        Array.isArray(ensureSummary?.ready_instruction_ids)
          ? ensureSummary.ready_instruction_ids.map((x: any) => Number(x || 0)).filter((x: number) => x > 0)
          : []
      );
      const failedRowsRaw = Array.isArray(ensureSummary?.failed_rows) ? ensureSummary.failed_rows : [];
      failedRowsRaw.forEach((r: any) => {
        const iid = Number(r?.instruction_id || 0);
        if (iid > 0) ensureFailedMap[iid] = String(r?.reason || '工单补齐失败');
      });
      for (const row of eligible) {
        if (ensureFailedMap[row.instructionId]) {
          failRows.push({
            code: row.instructionCode || `#${row.instructionId}`,
            reason: `工单补齐失败：${ensureFailedMap[row.instructionId]}`,
          });
          continue;
        }
        if (readySet.size > 0 && !readySet.has(row.instructionId)) {
          failRows.push({
            code: row.instructionCode || `#${row.instructionId}`,
            reason: '工单未就绪，已跳过',
          });
          continue;
        }
        try {
          const resp = await request.post(`/api/v1/instruction/instructions/${row.instructionId}/result/submit/`, {});
          const payload = resp?.data || {};
          const finalize = (payload?.inventory_finalize && typeof payload.inventory_finalize === 'object')
            ? payload.inventory_finalize
            : ((payload?.inventory_consume && typeof payload.inventory_consume === 'object') ? payload.inventory_consume : {});
          const effectivePolicy = (finalize?.effective_policy && typeof finalize.effective_policy === 'object')
            ? finalize.effective_policy
            : {};
          const modeRaw = String(finalize?.mode || effectivePolicy?.applied_mode || '').trim().toLowerCase();
          const sourceRaw = String(finalize?.source || effectivePolicy?.source || '').trim();
          const matchedRaw = (typeof finalize?.matched === 'boolean')
            ? finalize.matched
            : (typeof effectivePolicy?.matched === 'boolean' ? effectivePolicy.matched : null);
          const fallbackReasonRaw = String(effectivePolicy?.fallback_reason || '').trim();
          const modeText = (
            modeRaw === 'consume_reserved'
              ? '转消耗'
              : (modeRaw === 'release_reserved'
                ? '释放执行期占用'
                : (modeRaw === 'transfer_to_business_hold' ? '转业务留存占用' : (modeRaw || '-')))
          );
          const sourceText = (
            sourceRaw === 'final_review_finalize'
              ? '模板自定义'
              : (sourceRaw === 'final_review_consume'
                ? '模板旧配置'
                : (sourceRaw === 'default' ? '系统默认' : (sourceRaw || '-')))
          );
          finalizePolicyRows.push({
            code: row.instructionCode || `#${row.instructionId}`,
            mode: modeText,
            source: sourceText,
            matched: (typeof matchedRaw === 'boolean') ? (matchedRaw ? '命中' : '未命中') : '-',
            fallbackReason: fallbackReasonRaw || '-',
          });
          const createdRaw = payload?.billing_records_created ?? payload?.charges_created ?? 0;
          const createdNum = Number(createdRaw || 0);
          if (Number.isFinite(createdNum) && createdNum > 0) {
            okCodes.push(row.instructionCode || `#${row.instructionId}`);
          } else {
            const noBillingDetail = String(
              payload?.detail
              || payload?.message
              || payload?.pricing_detail
              || ''
            ).trim();
            noBillingRows.push({
              code: row.instructionCode || `#${row.instructionId}`,
              reason: noBillingDetail
                ? `终审成功但未生成收费记录（billing_records_created=0，${noBillingDetail}）`
                : '终审成功但未生成收费记录（billing_records_created=0）',
            });
          }
        } catch (e: any) {
          failRows.push({
            code: row.instructionCode || `#${row.instructionId}`,
            reason: String(e?.response?.data?.detail || e?.response?.data?.message || e?.message || '终审失败'),
          });
        }
      }
      const batchSystemCode = String(detail?.system_code || batchRow?.system_code || `#${batchId}`);
      const resultCopyText = [
        '批量终审执行结果',
        `批次系统编码：${batchSystemCode}`,
        ensureSummary
          ? `工单补齐：可处理${Number(ensureSummary?.eligible || 0)}条，已就绪${Number(ensureSummary?.already_ready || 0)}条，补齐成功${Number(ensureSummary?.ensured || 0)}条，失败${Number(ensureSummary?.failed || 0)}条`
          : '',
        `成功（已出账）：${okCodes.length} 条`,
        `未出账成功：${noBillingRows.length} 条`,
        `失败：${failRows.length} 条`,
        finalizePolicyRows.length > 0 ? '' : '',
        finalizePolicyRows.length > 0 ? '库存终态结算策略明细：' : '',
        ...finalizePolicyRows.slice(0, 50).map((f, idx) => `${idx + 1}. ${f.code}：模式=${f.mode} | 来源=${f.source} | 条件=${f.matched} | 回退原因=${f.fallbackReason}`),
        finalizePolicyRows.length > 50 ? '...其余策略明细请在终审页查看' : '',
        noBillingRows.length > 0 ? '' : '',
        noBillingRows.length > 0 ? '未出账成功指令明细：' : '',
        ...noBillingRows.slice(0, 50).map((f, idx) => `${idx + 1}. ${f.code}：${f.reason}`),
        noBillingRows.length > 50 ? '...其余未出账项请在终审页面检查计费事实/规则' : '',
        failRows.length > 0 ? '' : '',
        failRows.length > 0 ? '失败指令明细：' : '',
        ...failRows.slice(0, 50).map((f, idx) => `${idx + 1}. ${f.code}：${f.reason}`),
        failRows.length > 50 ? '...其余失败项请重试或在指令列表中逐条处理' : '',
      ].filter((x) => String(x || '').trim().length > 0).join('\n');
      const copyFinalReviewResult = async () => {
        try {
          await navigator.clipboard.writeText(resultCopyText);
          message.success('已复制终审结果');
        } catch {
          message.error('复制失败，请手动复制');
        }
      };
      Modal.info({
        title: '批量终审执行结果',
        width: 760,
        content: (
          <div style={{ maxHeight: 420, overflowY: 'auto', lineHeight: 1.8 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
              <Button size="small" icon={<CopyOutlined />} onClick={() => { void copyFinalReviewResult(); }}>
                复制结果
              </Button>
            </div>
            <div style={{ color: '#1677ff', fontWeight: 600, marginBottom: 6 }}>
              {`批次系统编码：${batchSystemCode}`}
            </div>
            {ensureSummary ? (
              <div style={{ marginBottom: 8 }}>
                {`工单补齐：可处理${Number(ensureSummary?.eligible || 0)}条，已就绪${Number(ensureSummary?.already_ready || 0)}条，补齐成功${Number(ensureSummary?.ensured || 0)}条，失败${Number(ensureSummary?.failed || 0)}条`}
              </div>
            ) : null}
            <div>{`成功（已出账）：${okCodes.length} 条`}</div>
            <div style={{ color: '#cf1322', fontWeight: 600 }}>{`未出账成功：${noBillingRows.length} 条`}</div>
            <div>{`失败：${failRows.length} 条`}</div>
            {finalizePolicyRows.length > 0 ? (
              <div style={{ marginTop: 8 }}>
                <div style={{ color: '#1677ff', fontWeight: 600, marginBottom: 4 }}>库存终态结算策略明细：</div>
                {finalizePolicyRows.slice(0, 50).map((f, idx) => (
                  <div key={`finalize-policy-${f.code}-${idx}`}>
                    {`${idx + 1}. ${f.code}：模式=${f.mode} | 来源=${f.source} | 条件=${f.matched} | 回退原因=${f.fallbackReason}`}
                  </div>
                ))}
                {finalizePolicyRows.length > 50 ? <div>...其余策略明细请在终审页查看</div> : null}
              </div>
            ) : null}
            {noBillingRows.length > 0 ? (
              <div style={{ marginTop: 8 }}>
                <div style={{ color: '#cf1322', fontWeight: 600, marginBottom: 4 }}>未出账成功指令明细：</div>
                {noBillingRows.slice(0, 50).map((f, idx) => (
                  <div key={`nobill-${f.code}-${idx}`} style={{ color: '#cf1322' }}>
                    {`${idx + 1}. ${f.code}：${f.reason}`}
                  </div>
                ))}
                {noBillingRows.length > 50 ? <div>...其余未出账项请在终审页面检查计费事实/规则</div> : null}
              </div>
            ) : null}
            {failRows.length > 0 ? (
              <div style={{ marginTop: 8 }}>
                {failRows.slice(0, 50).map((f, idx) => (
                  <div key={`${f.code}-${idx}`} style={{ color: '#cf1322' }}>
                    {`${idx + 1}. ${f.code}：${f.reason}`}
                  </div>
                ))}
                {failRows.length > 50 ? <div>...其余失败项请重试或在指令列表中逐条处理</div> : null}
              </div>
            ) : null}
          </div>
        ),
      });
      try {
        await request.post(`/api/v1/instruction/batches/${batchId}/final-review-log/`, {
          success_billing_count: okCodes.length,
          no_billing_count: noBillingRows.length,
          no_billing_codes: noBillingRows.map((x) => String(x?.code || '').trim()).filter(Boolean),
        });
      } catch {
        // 终审日志写入失败不阻断主流程
      }
      try { fetchData(); } catch {}
    } finally {
      setFinalReviewBatchId(null);
    }
  }, [fetchData, finalReviewBatchId]);

  useEffect(() => {
    fetchDataRef.current = () => {
      fetchData();
    };
  }, [fetchData]);

  const fetchOtherData = useCallback(async () => {
    if (batchTab !== 'other') return;
    setOtherLoading(true);
    try {
      const params: any = {
      };
      if (otherFilters.keyword) params.search = otherFilters.keyword;
      if (otherActiveStatus) params.status = otherActiveStatus;
      const fullList = await loadAllBatches(params);
      let results = Array.isArray(fullList) ? fullList : [];
      results = results.filter((r: any) => String(r?.template_system_code || '') !== DROPSHIP_TEMPLATE_CODE);
      const count = results.length || 0;
      const pageSize = Number(otherPagination.pageSize || 20) || 20;
      const maxPage = Math.max(1, Math.ceil(count / pageSize));
      const safeCurrent = Math.min(Number(otherPagination.current || 1) || 1, maxPage);
      const start = (safeCurrent - 1) * pageSize;
      const paged = results.slice(start, start + pageSize);
      setOtherRows(paged);
      setOtherPagination((prev) => ({ ...prev, current: safeCurrent, total: count }));
    } catch (e: any) {
      message.error(e?.message || '加载失败');
    } finally {
      setOtherLoading(false);
    }
  }, [batchTab, loadAllBatches, otherActiveStatus, otherFilters.keyword, otherPagination.current, otherPagination.pageSize]);

  const fetchTabCounts = useCallback(async () => {
    try {
      const commonParams: any = {};
      if (filters.keyword) commonParams.search = filters.keyword;
      if (searchOnlyBatchId) commonParams.id = searchOnlyBatchId;
      const mainList = await loadAllBatches(commonParams);
      const dropshipList = (mainList || []).filter((r: any) => String(r?.template_system_code || '') === DROPSHIP_TEMPLATE_CODE);
      const outboundList = (mainList || []).filter((r: any) => String(r?.template_system_code || '') !== DROPSHIP_TEMPLATE_CODE);
      setDropshipStatusCounts(tallyStatusCounts(dropshipList));
      setOutboundStatusCounts(tallyStatusCounts(outboundList));

      const otherAll = await loadAllBatches({ ...(otherFilters.keyword ? { search: otherFilters.keyword } : {}) });
      const otherList = (otherAll || []).filter((r: any) => String(r?.template_system_code || '') !== DROPSHIP_TEMPLATE_CODE);
      setOtherStatusCounts(tallyStatusCounts(otherList));
      setMainTabCounts({
        dropship: dropshipList.length,
        outbound: outboundList.length,
        other: otherList.length,
      });
    } catch {
      // ignore count load errors
    }
  }, [filters.keyword, loadAllBatches, otherFilters.keyword, searchOnlyBatchId, tallyStatusCounts]);

  useEffect(() => { if (batchTab !== 'other') fetchData(); }, [fetchData, batchTab]);
  useEffect(() => { if (batchTab === 'other') fetchOtherData(); }, [fetchOtherData, batchTab]);
  useEffect(() => { fetchTabCounts(); }, [fetchTabCounts]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [batchTab]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(String(window?.location?.search || ''));
      const tpl = params.get('template_system_code');
      if (tpl) {
        if (tpl === DROPSHIP_TEMPLATE_CODE) setBatchTab('dropship');
        else setBatchTab('other');
      }
    } catch {}
  }, []);

  const onSearch = async () => {
    try {
      const v = await form.validateFields();
      setFilters({
        keyword: v.keyword?.trim() || '',
        template_system_code: v.template_system_code?.trim() || '',
        instruction_system_code: v.instruction_system_code?.trim() || '',
      });
      setPagination((prev) => ({ ...prev, current: 1 }));
    } catch {}
  };

  const onSearchByInstructionCode = async () => {
    const code = String(searchInstrCode || '').trim();
    if (!code) {
      setSearchOnlyBatchId(null);
      setSearchInstrCode('');
      setPagination((prev) => ({ ...prev, current: 1 }));
      return;
    }
    try {
      const resp = await request.get('/api/v1/instruction/batches/by-instruction-code/', {
        params: { instruction_system_code: code },
      });
      const batchId = Number(resp?.data?.batch_id);
      const status = String(resp?.data?.status || '').trim();
      if (!batchId || !status) {
        message.warning('未找到对应批次');
        return;
      }
      setSearchOnlyBatchId(batchId);
      setActiveStatus(status);
      setPagination((prev) => ({ ...prev, current: 1 }));
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '查询失败');
    }
  };


  const onSearchOther = async () => {
    try {
      const v = await otherForm.validateFields();
      setOtherFilters({
        keyword: v.keyword?.trim() || '',
      });
      setOtherPagination((prev) => ({ ...prev, current: 1 }));
    } catch {}
  };

  const formatKindQty = useCallback((raw: any) => {
    const s = String(raw || '').trim();
    if (!s) return [];
    const [k, q] = s.split('|').map((v) => v.trim());
    return [
      { label: '种类', val: k || '0' },
      { label: '数量', val: q || '0' },
    ];
  }, []);

  const formatWaybillSummary = useCallback((raw: any) => {
    const data = raw && typeof raw === 'object' ? raw : null;
    if (!data) return [];
    const rows = [
      { label: '有运单', val: String((data as any).upload_immediate || '').trim() },
      { label: '仓库提供运单', val: String((data as any).warehouse || '').trim() },
      { label: '先申请包装数据', val: String((data as any).upload_after_pack || '').trim() },
    ];
    return rows.filter((r) => {
      const m = String(r.val || '').match(/^(\d+)\s*单/);
      const orders = m ? Number(m[1]) : 0;
      return orders > 0;
    });
  }, []);

  const formatPackSummary = useCallback((raw: any) => {
    const s = String(raw || '').trim();
    if (!s) return [];
    const [a, b] = s.split('|').map((v) => v.trim());
    return [
      { label: '无需打包', val: a || '-' },
      { label: '需打包', val: b || '-' },
    ];
  }, []);

  const formatSkuMixSummary = useCallback((raw: any) => {
    const s = String(raw || '').trim();
    if (!s) return [];
    const parts = s.split('|').map((v) => v.trim());
    const labels = ['单款单件', '单款多件', '多款多件'];
    return parts
      .map((val, idx) => ({ label: labels[idx] || '', val }))
      .filter((item) => item.val && item.val !== '-' && item.label);
  }, []);

  const columns = useMemo(() => ([
    {
      title: '批次编码',
      dataIndex: 'system_code',
      key: 'system_code',
      width: 140,
      render: (v: any) => (v ? <Text code copyable={{ text: String(v) }}>{v}</Text> : '-'),
    },
    {
      title: '指令数量',
      key: 'items',
      width: 100,
      render: (_: any, r: any) => (Array.isArray(r?.items) ? r.items.length : 0),
    },
    {
      title: '库存单元',
      key: 'inventory_unit_summary',
      width: 140,
      render: (_: any, r: any) => {
        const rows = formatKindQty(r?.inventory_unit_summary);
        if (!rows.length) return '-';
        const detail = Array.isArray(r?.inventory_unit_detail) ? r.inventory_unit_detail : [];
        const body = (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {rows.map((item) => (
              <div key={item.label}>{`${item.label}-${item.val}${item.label === '种类' ? '种' : '件'}`}</div>
            ))}
          </div>
        );
        if (!detail.length) return body;
        return (
          <Button
            type="link"
            style={{ padding: 0, height: 'auto', textAlign: 'left' }}
            onClick={() => {
              setInventoryModalRows(detail);
              setInventoryModalTitle(`库存单元汇总${r?.system_code ? ` - ${r.system_code}` : ''}`);
              setInventoryModalOpen(true);
            }}
          >
            {body}
          </Button>
        );
      },
    },
    {
      title: '承运商分布',
      key: 'carrier_kind_count',
      width: 160,
      render: (_: any, r: any) => {
        const detail = Array.isArray(r?.carrier_kind_detail) ? r.carrier_kind_detail : [];
        if (detail.length === 0) return '-';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {detail.map((item: any, idx: number) => (
              <div key={`${item?.carrier || '-'}-${idx}`}>
                {`${item?.carrier || '-'}-${Number(item?.count || 0)}单${Number(item?.qty || 0)}件`}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: '指令类型分布',
      key: 'sku_mix',
      width: 180,
      render: (_: any, r: any) => {
        const rows = formatSkuMixSummary(r?.sku_mix_summary);
        if (!rows.length) return '-';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {rows.map((item) => (
              <div key={item.label}>{`${item.label}-${item.val}`}</div>
            ))}
          </div>
        );
      },
    },
    {
      title: '是否需打包',
      key: 'packing_required',
      width: 160,
      render: (_: any, r: any) => {
        const rows = formatPackSummary(r?.pack_required_summary);
        if (!rows.length) return '-';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {rows.map((item) => (
              <div key={item.label}>{`${item.label}-${item.val}`}</div>
            ))}
          </div>
        );
      },
    },
    {
      title: '运单状态分布',
      key: 'waybill_mode',
      width: 180,
      render: (_: any, r: any) => {
        const rows = formatWaybillSummary(r?.waybill_mode_detail);
        if (!rows.length) return '-';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {rows.map((item) => (
              <div key={item.label}>{`${item.label}-${item.val}`}</div>
            ))}
          </div>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      render: (v: any) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right' as const,
      width: 140,
      render: (_: any, r: any) => (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, max-content)', columnGap: 8, rowGap: 4 }}>
          <Button
            type="link"
            onClick={() => {
              try {
                const path = String((window as any)?.location?.pathname || '');
                const base = path.startsWith('/dashboard/staff')
                  ? '/dashboard/staff'
                  : path.startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/customer';
                navigate(`${base}/instructions/batch/${r?.id}`);
              } catch {}
            }}
          >
            查看
          </Button>
          {String(r?.status || '') === 'draft' ? (
            <Button
              type="link"
              onClick={async () => {
                try {
                  setPrecheckTitle(`批次预审${r?.system_code ? ` - ${r.system_code}` : ''}`);
                  setPrecheckActive('upload_immediate');
                  setPrecheckOpen(true);
                  setPrecheckBatchId(Number(r?.id) || null);
                  await loadPrecheckRows(r?.id);
                } catch (e: any) {
                  message.error(e?.message || '加载预审列表失败');
                }
              }}
            >
              预审
            </Button>
          ) : null}
          {String(r?.status || '') === 'draft' ? (
            <Button
              type="link"
              onClick={() => {
                Modal.confirm({
                  title: '确认取消该批次?',
                  okText: '确认取消',
                  cancelText: '再想想',
                  onOk: async () => {
                    try {
                      await request.post(`/api/v1/instruction/batches/${r?.id}/cancel/`);
                      message.success('已取消');
                      fetchData();
                    } catch (e: any) {
                      message.error(e?.message || '取消失败');
                    }
                  },
                });
              }}
            >
              取消
            </Button>
          ) : null}
          {(String(r?.status || '') === 'executed' && batchTab === 'dropship' && activeStatus === 'executed') ? (
            <Button
              type="default"
              style={(r?.meta && r.meta.final_review_done)
                ? { background: '#fa8c16', borderColor: '#fa8c16', color: '#fff' }
                : undefined}
              loading={finalReviewBatchId === Number(r?.id || 0)}
              disabled={(!!finalReviewBatchId && finalReviewBatchId !== Number(r?.id || 0)) || !!(r?.meta && r.meta.final_review_done)}
              onClick={async () => {
                await handleBatchFinalReview(r);
              }}
            >
              {(r?.meta && r.meta.final_review_done) ? '已完成终审' : '批量终审'}
            </Button>
          ) : null}
        </div>
      ),
    },
  ]), [activeStatus, batchTab, finalReviewBatchId, formatKindQty, formatSkuMixSummary, formatPackSummary, formatWaybillSummary, handleBatchFinalReview, openExecReminder, precheckExecutionMap]);

  const otherColumns = useMemo(() => ([
    {
      title: '批次编码',
      dataIndex: 'system_code',
      key: 'system_code',
      width: 140,
      render: (v: any) => (v ? <Text code copyable={{ text: String(v) }}>{v}</Text> : '-'),
    },
    {
      title: '指令数量',
      key: 'items',
      width: 100,
      render: (_: any, r: any) => (Array.isArray(r?.items) ? r.items.length : 0),
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      render: () => (
        <Space size={8}>
          <Button type="link">查看</Button>
        </Space>
      ),
    },
  ]), []);

  const buildTabPillStyle = useCallback((
    active: boolean,
    tone: { color: string; border: string; bg: string },
    compact = false,
  ): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: compact ? '4px 10px' : '6px 10px',
    borderRadius: 6,
    border: active ? `1px solid ${tone.border}` : '1px solid #d9d9d9',
    background: active ? tone.bg : '#ffffff',
    color: active ? tone.color : '#595959',
    fontWeight: active ? 700 : 500,
    lineHeight: 1.2,
  }), []);

  return (
    <div className="list-page-container">
      <Card>
        <style>{`
          .batch-main-tabs .ant-tabs-ink-bar,
          .batch-sub-tabs .ant-tabs-ink-bar {
            display: none !important;
          }
          .batch-main-tabs .ant-tabs-nav::before,
          .batch-sub-tabs .ant-tabs-nav::before {
            border-bottom: none !important;
          }
          .batch-main-tabs .ant-tabs-tab,
          .batch-sub-tabs .ant-tabs-tab {
            margin: 0 !important;
            padding: 0 !important;
          }
          .batch-main-tabs .ant-tabs-tab + .ant-tabs-tab,
          .batch-sub-tabs .ant-tabs-tab + .ant-tabs-tab {
            margin-left: 8px !important;
          }
        `}</style>
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Title level={4} style={{ margin: 0 }}>批次管理</Title>
          <Button icon={<ReloadOutlined />} onClick={fetchData}>刷新</Button>
        </div>

        <Tabs
          className="batch-main-tabs"
          activeKey={batchTab}
          onChange={(key) => setBatchTab(key as 'dropship' | 'outbound' | 'other')}
          items={[
            {
              key: 'dropship',
              label: (
                <span style={buildTabPillStyle(batchTab === 'dropship', { color: '#1890ff', border: '#91d5ff', bg: '#e6f7ff' })}>
                  <span>批次管理-一件代发指令</span>
                  <Badge
                    count={mainTabCounts.dropship || 0}
                    showZero
                    style={{ backgroundColor: batchTab === 'dropship' ? '#1890ff' : '#bfbfbf', borderRadius: 10 }}
                  />
                </span>
              ),
              children: (
                <>
                  <Form form={form} layout="inline" style={{ marginBottom: 16 }}>
                    <Form.Item label="指令系统编码">
                      <Input
                        allowClear
                        placeholder="输入指令系统编码"
                        value={searchInstrCode}
                        onChange={(e) => setSearchInstrCode(e.target.value)}
                        onPressEnter={onSearchByInstructionCode}
                        style={{ width: 220 }}
                      />
                    </Form.Item>
                    <Form.Item>
                      <Button onClick={onSearchByInstructionCode}>查找所属批次</Button>
                    </Form.Item>
                    <Form.Item>
                      <Button
                        onClick={() => {
                          setSearchOnlyBatchId(null);
                          setSearchInstrCode('');
                          setPagination((prev) => ({ ...prev, current: 1 }));
                          fetchData();
                        }}
                      >
                        清除筛选恢复列表
                      </Button>
                    </Form.Item>
                  </Form>

                  <Tabs
                    className="batch-sub-tabs"
                    activeKey={activeStatus}
                    onChange={(key) => {
                      setActiveStatus(key);
                      setPagination((prev) => ({ ...prev, current: 1 }));
                    }}
                    items={statusTabs.map((t) => ({
                      key: t.key,
                      label: (
                        <span style={buildTabPillStyle(activeStatus === t.key, { color: '#1890ff', border: '#91d5ff', bg: '#e6f7ff' }, true)}>
                          <span>{t.label}</span>
                          <Badge
                            count={dropshipStatusCounts[t.key] ?? 0}
                            showZero
                            style={{ backgroundColor: activeStatus === t.key ? '#1890ff' : '#bfbfbf', borderRadius: 10 }}
                          />
                        </span>
                      ),
                      children: (
                        <Table
                          rowKey={(r: any) => String(r?.id ?? '')}
                          dataSource={rows}
                          columns={columns as any}
                          loading={loading}
                          pagination={false}
                          scroll={{ x: 1200 }}
                        />
                      ),
                    }))}
                  />

                  <div className="flex justify-end items-center" style={{ marginTop: 16 }}>
                    <Space size={12}>
                      <span className="text-gray-600">
                        {(() => {
                          const current = pagination.current;
                          const pageSize = pagination.pageSize;
                          const total = pagination.total;
                          const rowsOnPage = Array.isArray(rows) ? rows.length : 0;
                          const start = total > 0 ? ((current - 1) * pageSize) + 1 : 0;
                          const end = total > 0 ? start + Math.max(rowsOnPage - 1, 0) : 0;
                          return `第 ${start} - ${end} 条， 共 ${total} 条记录`;
                        })()}
                      </span>
                      <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={pagination.total}
                        showSizeChanger
                        showQuickJumper
                        pageSizeOptions={[10, 20, 50, 100] as any}
                        onChange={(page, pageSize) => {
                          setPagination((prev) => ({ ...prev, current: page, pageSize: pageSize || prev.pageSize }));
                        }}
                      />
                    </Space>
                  </div>
                </>
              ),
            },
            {
              key: 'outbound',
              label: (
                <span style={buildTabPillStyle(batchTab === 'outbound', { color: '#fa8c16', border: '#ffd591', bg: '#fff7e6' })}>
                  <span>批次管理-出库指令</span>
                  <Badge
                    count={mainTabCounts.outbound || 0}
                    showZero
                    style={{ backgroundColor: batchTab === 'outbound' ? '#fa8c16' : '#bfbfbf', borderRadius: 10 }}
                  />
                </span>
              ),
              children: (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <Title level={5} style={{ margin: 0 }}>出库批次管理</Title>
                  </div>
                  <Form form={form} layout="inline" style={{ marginBottom: 16 }}>
                    <Form.Item name="keyword" label="关键词">
                      <Input
                        allowClear
                        placeholder="批次编码/批次名称"
                        onPressEnter={onSearch}
                        style={{ width: 220 }}
                      />
                    </Form.Item>
                    <Form.Item name="template_system_code" label="指令模板系统编码">
                      <Input
                        allowClear
                        placeholder="如 InstructionTemplate-SYS-XX"
                        onPressEnter={onSearch}
                        style={{ width: 260 }}
                      />
                    </Form.Item>
                    <Form.Item>
                      <Button type="primary" icon={<SearchOutlined />} onClick={onSearch}>搜索</Button>
                    </Form.Item>
                    <Form.Item>
                      <Button
                        onClick={() => {
                          try { form.resetFields(['keyword', 'template_system_code']); } catch {}
                          setFilters((prev) => ({ ...prev, keyword: '', template_system_code: '' }));
                          setSearchOnlyBatchId(null);
                          setPagination((prev) => ({ ...prev, current: 1 }));
                        }}
                      >
                        重置
                      </Button>
                    </Form.Item>
                  </Form>
                  <Tabs
                    className="batch-sub-tabs"
                    activeKey={activeStatus}
                    onChange={(key) => {
                      setActiveStatus(key);
                      setPagination((prev) => ({ ...prev, current: 1 }));
                    }}
                    items={statusTabs.map((t) => ({
                      key: t.key,
                      label: (
                        <span style={buildTabPillStyle(activeStatus === t.key, { color: '#fa8c16', border: '#ffd591', bg: '#fff7e6' }, true)}>
                          <span>{t.label}</span>
                          <Badge
                            count={outboundStatusCounts[t.key] ?? 0}
                            showZero
                            style={{ backgroundColor: activeStatus === t.key ? '#fa8c16' : '#bfbfbf', borderRadius: 10 }}
                          />
                        </span>
                      ),
                      children: (
                        <Table
                          rowKey={(r: any) => String(r?.id ?? '')}
                          dataSource={rows}
                          columns={columns as any}
                          loading={loading}
                          pagination={false}
                          scroll={{ x: 1200 }}
                        />
                      ),
                    }))}
                  />
                  <div className="flex justify-end items-center" style={{ marginTop: 16 }}>
                    <Space size={12}>
                      <span className="text-gray-600">
                        {(() => {
                          const current = pagination.current;
                          const pageSize = pagination.pageSize;
                          const total = pagination.total;
                          const rowsOnPage = Array.isArray(rows) ? rows.length : 0;
                          const start = total > 0 ? ((current - 1) * pageSize) + 1 : 0;
                          const end = total > 0 ? start + Math.max(rowsOnPage - 1, 0) : 0;
                          return `第 ${start} - ${end} 条， 共 ${total} 条记录`;
                        })()}
                      </span>
                      <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={pagination.total}
                        showSizeChanger
                        showQuickJumper
                        pageSizeOptions={[10, 20, 50, 100] as any}
                        onChange={(page, pageSize) => {
                          setPagination((prev) => ({ ...prev, current: page, pageSize: pageSize || prev.pageSize }));
                        }}
                      />
                    </Space>
                  </div>
                </>
              ),
            },
            {
              key: 'other',
              label: (
                <span style={buildTabPillStyle(batchTab === 'other', { color: '#52c41a', border: '#b7eb8f', bg: '#f6ffed' })}>
                  <span>批次管理-其他类型指令</span>
                  <Badge
                    count={mainTabCounts.other || 0}
                    showZero
                    style={{ backgroundColor: batchTab === 'other' ? '#52c41a' : '#bfbfbf', borderRadius: 10 }}
                  />
                </span>
              ),
              children: (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <Title level={5} style={{ margin: 0 }}>其他类型批次管理</Title>
                  </div>
                  <Form form={otherForm} layout="inline" style={{ marginBottom: 16 }}>
                    <Form.Item name="keyword" label="关键词">
                      <Input
                        allowClear
                        placeholder="批次编码/名称"
                        onPressEnter={onSearchOther}
                        style={{ width: 220 }}
                      />
                    </Form.Item>
                    <Form.Item>
                      <Button type="primary" icon={<SearchOutlined />} onClick={onSearchOther}>搜索</Button>
                    </Form.Item>
                  </Form>

                  <Tabs
                    className="batch-sub-tabs"
                    activeKey={otherActiveStatus}
                    onChange={(key) => {
                      setOtherActiveStatus(key);
                      setOtherPagination((prev) => ({ ...prev, current: 1 }));
                    }}
                    items={statusTabs.map((t) => ({
                      key: t.key,
                      label: (
                        <span style={buildTabPillStyle(otherActiveStatus === t.key, { color: '#52c41a', border: '#b7eb8f', bg: '#f6ffed' }, true)}>
                          <span>{t.label}</span>
                          <Badge
                            count={otherStatusCounts[t.key] ?? 0}
                            showZero
                            style={{ backgroundColor: otherActiveStatus === t.key ? '#52c41a' : '#bfbfbf', borderRadius: 10 }}
                          />
                        </span>
                      ),
                      children: (
                        <Table
                          rowKey={(r: any) => String(r?.id ?? '')}
                          dataSource={otherRows}
                          columns={otherColumns as any}
                          loading={otherLoading}
                          pagination={false}
                          scroll={{ x: 520 }}
                        />
                      ),
                    }))}
                  />

                  <div className="flex justify-end items-center" style={{ marginTop: 16 }}>
                    <Space size={12}>
                      <span className="text-gray-600">
                        {(() => {
                          const current = otherPagination.current;
                          const pageSize = otherPagination.pageSize;
                          const total = otherPagination.total;
                          const rowsOnPage = Array.isArray(otherRows) ? otherRows.length : 0;
                          const start = total > 0 ? ((current - 1) * pageSize) + 1 : 0;
                          const end = total > 0 ? start + Math.max(rowsOnPage - 1, 0) : 0;
                          return `第 ${start} - ${end} 条， 共 ${total} 条记录`;
                        })()}
                      </span>
                      <Pagination
                        current={otherPagination.current}
                        pageSize={otherPagination.pageSize}
                        total={otherPagination.total}
                        showSizeChanger
                        showQuickJumper
                        pageSizeOptions={[10, 20, 50, 100] as any}
                        onChange={(page, pageSize) => {
                          setOtherPagination((prev) => ({ ...prev, current: page, pageSize: pageSize || prev.pageSize }));
                        }}
                      />
                    </Space>
                  </div>
                </>
              ),
            },
          ]}
        />
        <Modal
          open={inventoryModalOpen}
          title={(
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 32 }}>
              <span>{inventoryModalTitle || '库存单元汇总'}</span>
              <Button
                size="small"
                onClick={async () => {
                  try {
                    const header = ['库存单元编码', '分拣条码', '客户自定义编码', '可用库存数量', '发货总数量', '库存是否充足'].join('\t');
                    const lines = (inventoryModalRows || []).map((r: any) => ([
                      r?.inventory_unit_code || '-',
                      r?.sorting_barcodes || '-',
                      r?.custom_codes || '-',
                      Number(r?.available_qty || 0),
                      r?.ship_qty || '0',
                      r?.is_sufficient ? '充足' : '不足',
                    ].join('\t')));
                    const text = [header, ...lines].join('\n');
                    await navigator.clipboard.writeText(text);
                    message.success('已复制');
                  } catch {
                    message.error('复制失败');
                  }
                }}
              >
                复制
              </Button>
            </div>
          )}
          footer={null}
          width={1120}
          onCancel={() => setInventoryModalOpen(false)}
        >
          <Table
            rowKey={(r: any) => String(r?.inventory_unit_id || '')}
            dataSource={inventoryModalRows}
            pagination={false}
            columns={[
              { title: '库存单元编码', dataIndex: 'inventory_unit_code', key: 'inventory_unit_code', width: 140, render: (v: any) => v || '-' },
              { title: '分拣条码', dataIndex: 'sorting_barcodes', key: 'sorting_barcodes', width: 160, render: (v: any) => v || '-' },
              { title: '客户自定义编码', dataIndex: 'custom_codes', key: 'custom_codes', width: 160, render: (v: any) => v || '-' },
              { title: '创建批次前可用库存数量', dataIndex: 'available_qty', key: 'available_qty', width: 180, render: (v: any) => (Number(v) || 0) },
              { title: '发货总数量', dataIndex: 'ship_qty', key: 'ship_qty', width: 120, render: (v: any) => v || '0' },
              {
                title: '库存是否充足',
                dataIndex: 'is_sufficient',
                key: 'is_sufficient',
                width: 120,
                render: (v: any) => (
                  <Tag color={v ? 'green' : 'red'}>{v ? '充足' : '不足'}</Tag>
                ),
              },
            ]}
          />
        </Modal>
        <Modal
          open={precheckOpen}
          title={(
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 8 }}>
              <div>
                <span>{precheckTitle || '批次预审'}</span>
              </div>
              <div style={{ textAlign: 'center', display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  style={{ background: '#e6f7ff', borderColor: '#91d5ff', color: '#1890ff' }}
                >
                  {`共${precheckStats.totalOrders}单 ${precheckStats.totalQty}件`}
                </Button>
                <Button
                  size="small"
                  style={{ background: '#f6ffed', borderColor: '#b7eb8f', color: '#52c41a' }}
                >
                  {`已标注通过${precheckMarkStats.pass}单`}
                </Button>
                <Button
                  size="small"
                  style={{ background: '#fff1f0', borderColor: '#ffa39e', color: '#ff4d4f' }}
                >
                  {`已标注驳回${precheckMarkStats.reject}单`}
                </Button>
                <Button
                  size="small"
                  style={{ background: '#fafafa', borderColor: '#d9d9d9', color: '#595959' }}
                >
                  {`未标注${precheckMarkStats.none}单`}
                </Button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginRight: 95 }}>
                <Button
                  size="small"
                  type="primary"
                  loading={completePrecheckLoading}
                  disabled={precheckMarkStats.none !== 0 || completePrecheckLoading}
                  onClick={handleCompletePrecheck}
                >
                  完成预审
                </Button>
              </div>
            </div>
          )}
          footer={null}
          width={1500}
          onCancel={() => setPrecheckOpen(false)}
        >
          <Tabs
            activeKey={precheckActive}
            onChange={(k) => setPrecheckActive(k)}
            items={[
              { key: 'upload_immediate', label: `已上传运单(${precheckStats.uploadImmediate.orders}单${precheckStats.uploadImmediate.qty}件)` },
              { key: 'warehouse', label: `仓库提供运单(${precheckStats.warehouse.orders}单${precheckStats.warehouse.qty}件)` },
              { key: 'upload_after_pack', label: `先申请包装数据(${precheckStats.uploadAfterPack.orders}单${precheckStats.uploadAfterPack.qty}件)` },
            ]}
          />
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Space>
              <span>
                {`已选择${getSelectedKeysForTab(precheckActive, precheckSortedRows).length}条记录:`}
              </span>
              <Button
                disabled={getSelectedKeysForTab(precheckActive, precheckSortedRows).length === 0}
                onClick={() => {
                  const ids = getSelectedKeysForTab(precheckActive, precheckSortedRows);
                  if (!ids.length) return;
                  openExecReminderForIds(ids, precheckSortedRows);
                }}
              >
                批量标记通过
              </Button>
              <Button
                disabled={getSelectedKeysForTab(precheckActive, precheckSortedRows).length === 0}
                onClick={() => {
                  if (precheckActive === 'warehouse') {
                    setRejectReasonOptions([
                      { label: '邮编与地址不匹配', value: '邮编与地址不匹配' },
                      { label: '其他', value: '其他' },
                    ]);
                  } else if (precheckActive === 'upload_after_pack') {
                    setRejectReasonOptions([
                      { label: '无需打包', value: '无需打包' },
                      { label: '未备注特殊包装具体要求', value: '未备注特殊包装具体要求' },
                      { label: '其他', value: '其他' },
                    ]);
                  } else {
                    setRejectReasonOptions([
                      { label: '物流信息重复', value: '物流信息重复' },
                      { label: '跟踪号历史重复', value: '跟踪号历史重复' },
                      { label: '运单审核失败', value: '运单审核失败' },
                      { label: '其他', value: '其他' },
                    ]);
                  }
                  const ids = getSelectedKeysForTab(precheckActive, precheckSortedRows);
                  if (!ids.length) return;
                  const codes: string[] = [];
                  precheckSortedRows.forEach((r: any) => {
                    const id = String(r?.id ?? '');
                    if (ids.includes(id)) {
                      const code = String(r?.instruction_system_code || '');
                      if (code) codes.push(code);
                    }
                  });
                  if (precheckActive === 'warehouse') {
                    const mapped = (precheckRiskMap[String(codes[0] || '')] || []).map((x) => String(x || '').trim()).filter(Boolean);
                    let otherText = '';
                    const reasons: string[] = [];
                    mapped.forEach((it) => {
                      if (it.startsWith('其他:')) {
                        reasons.push('其他');
                        otherText = it.replace(/^其他:/, '').trim();
                      } else if (it === '邮编与地址不匹配') {
                        reasons.push('邮编与地址不匹配');
                      } else {
                        reasons.push('其他');
                        otherText = it;
                      }
                    });
                    setRejectReasons(Array.from(new Set(reasons)));
                    setRejectOtherText(otherText);
                  } else if (precheckActive === 'upload_after_pack') {
                    const mapped = (precheckRiskMap[String(codes[0] || '')] || []).map((x) => String(x || '').trim()).filter(Boolean);
                    let otherText = '';
                    const reasons: string[] = [];
                    mapped.forEach((it) => {
                      if (it.startsWith('其他:')) {
                        reasons.push('其他');
                        otherText = it.replace(/^其他:/, '').trim();
                      } else if (it === '无需打包') {
                        reasons.push('无需打包');
                      } else if (it === '未备注特殊包装具体要求') {
                        reasons.push('未备注特殊包装具体要求');
                      } else {
                        reasons.push('其他');
                        otherText = it;
                      }
                    });
                    setRejectReasons(Array.from(new Set(reasons)));
                    setRejectOtherText(otherText);
                  }
                  setRejectTargetIds(ids);
                  setRejectTargetCodes(codes);
                  if (precheckActive !== 'warehouse' && precheckActive !== 'upload_after_pack') {
                    setRejectReasons([]);
                    setRejectOtherText('');
                  }
                  setRejectModalOpen(true);
                }}
              >
                批量标记驳回
              </Button>
            </Space>
            <Button
              type="primary"
              loading={autoPrecheckRunning && precheckActive === 'upload_immediate'}
              onClick={() => {
                if (precheckActive === 'upload_immediate') {
                  runAutoPrecheckUploadImmediate();
                  return;
                }
                if (precheckActive === 'warehouse') {
                  runAutoPrecheckWarehouse();
                  return;
                }
                if (precheckActive === 'upload_after_pack') {
                  runAutoPrecheckAfterPack();
                  return;
                }
                message.warning('该功能仅适用于“已上传运单/仓库提供运单/先申请包装数据”');
              }}
            >
              {precheckActive === 'warehouse'
                ? '自动预审(仓库提供运单)'
                : precheckActive === 'upload_after_pack'
                  ? '自动预审(先申请包装数据)'
                  : '自动预审(已上传运单)'}
            </Button>
          </div>
          <Table
            rowKey={(r: any) => String(r?.id ?? '')}
            loading={precheckLoading}
            dataSource={precheckSortedRows}
            scroll={precheckActive === 'warehouse' ? { x: 'max-content' } : undefined}
            rowSelection={{
              selectedRowKeys: getSelectedKeysForTab(precheckActive, precheckSortedRows),
              onSelectAll: (selected) => {
                setPrecheckSelections((prev) => {
                  const next = { ...prev };
                  if (selected) {
                    next[precheckActive] = { allSelected: true, excluded: new Set<string>(), included: new Set<string>() };
                  } else {
                    next[precheckActive] = { allSelected: false, excluded: new Set<string>(), included: new Set<string>() };
                  }
                  return next;
                });
              },
              onChange: (_keys, selectedRows) => {
                setPrecheckSelections((prev) => {
                  const state = getSelectionState(precheckActive);
                  const next = { ...prev };
                  if (state.allSelected) {
                    const selectedIds = new Set(selectedRows.map((r: any) => String(r?.id ?? '')));
                    const allIds = precheckSortedRows.map((r: any) => String(r?.id ?? '')).filter(Boolean);
                    const excluded = new Set<string>();
                    for (const id of allIds) {
                      if (!selectedIds.has(id)) excluded.add(id);
                    }
                    next[precheckActive] = { allSelected: true, excluded, included: new Set<string>() };
                  } else {
                    const included = new Set(selectedRows.map((r: any) => String(r?.id ?? '')));
                    next[precheckActive] = { allSelected: false, excluded: new Set<string>(), included };
                  }
                  return next;
                });
              },
            }}
            pagination={{
              current: precheckPage,
              pageSize: precheckPageSize,
              total: precheckSortedRows.length,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: [10, 20, 50, 100] as any,
              showTotal: (total, range) => `第 ${range[0]} - ${range[1]} 条， 共 ${total} 条记录`,
              onChange: (page, pageSize) => {
                setPrecheckPage(page);
                if (pageSize) setPrecheckPageSize(pageSize);
              },
            }}
            onRow={(record: any) => ({
              style: { background: getCarrierBg(record?.waybill_carrier) },
            })}
            columns={precheckActive === 'upload_immediate' ? [
              {
                title: '指令系统编码',
                dataIndex: 'instruction_system_code',
                key: 'instruction_system_code',
                width: 180,
                render: (v: any) => {
                  const code = v || '-';
                  return (
                    <Space size={6}>
                      <span>{code}</span>
                      {code !== '-' ? (
                        <Tooltip title="复制">
                          <CopyOutlined
                            onClick={async (e) => {
                              e?.stopPropagation?.();
                              try {
                                await navigator.clipboard.writeText(String(code));
                                message.success('已复制');
                              } catch {
                                message.error('复制失败');
                              }
                            }}
                          />
                        </Tooltip>
                      ) : null}
                    </Space>
                  );
                },
              },
              {
                title: '发货产品信息',
                key: 'shipping_products',
                width: 360,
                render: (_: any, r: any) => {
                  const targets = Array.isArray(r?.targets_detail) ? r.targets_detail : [];
                  if (!targets.length) return '-';
                  const seen = new Set<string>();
                  const parts = targets.map((t: any) => {
                    const snap = t?.snapshot || {};
                    const code = t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || snap?.code || t?.inventory_unit_id || '-';
                    const sort = (Array.isArray(t?.sorting_barcodes) && t.sorting_barcodes.length > 0)
                      ? t.sorting_barcodes[0]
                      : (Array.isArray(t?.sample_item_barcodes) && t.sample_item_barcodes.length > 0)
                        ? t.sample_item_barcodes[0]
                        : (Array.isArray(snap?.sorting_barcodes) && snap.sorting_barcodes.length > 0 ? snap.sorting_barcodes[0]?.code : (snap?.sorting_barcode || ''));
                    const qty = Number(t?.expected_qty || 0);
                    const key = `${code}|${sort}|${qty}`;
                    if (seen.has(key)) return null;
                    seen.add(key);
                    const codeText = String(code || '-');
                    const sortText = sort ? `(${sort})` : '';
                    return `${codeText}${sortText}×${isNaN(qty) ? 0 : qty}`;
                  }).filter(Boolean);
                  return (
                    <div>
                      {(parts as string[]).map((line, idx) => (
                        <div key={`${line}-${idx}`}>{line}</div>
                      ))}
                    </div>
                  );
                },
              },
              { title: '物流承运商', dataIndex: 'waybill_carrier', key: 'waybill_carrier', width: 140, render: (v: any) => v || '-' },
              {
                title: '运单跟踪号',
                dataIndex: 'waybill_tracking_no',
                key: 'waybill_tracking_no',
                width: 220,
                render: (v: any) => {
                  const raw = v ? String(v).trim().toUpperCase() : '';
                  if (!raw) return '-';
                  const display = raw.length > 22 ? `${raw.slice(0, 22)}...` : raw;
                  return (
                    <Space size={6}>
                      <Tooltip title={raw}>
                        <span>{display}</span>
                      </Tooltip>
                      <Tooltip title="复制">
                        <CopyOutlined
                          onClick={async (e) => {
                            e?.stopPropagation?.();
                            try {
                              await navigator.clipboard.writeText(raw);
                              message.success('已复制');
                            } catch {
                              message.error('复制失败');
                            }
                          }}
                        />
                      </Tooltip>
                    </Space>
                  );
                },
              },
              {
                title: '运单附件',
                key: 'waybill_attachments',
                width: 220,
                render: (_: any, r: any) => {
                  const list = Array.isArray(r?.attachments_detail) ? r.attachments_detail : [];
                  const filtered = list.filter((a: any) => {
                    const label = String(a?.label || '').toLowerCase();
                    return label.includes('运单') || label.includes('waybill') || label.includes('tracking');
                  });
                  const useList = filtered.length ? filtered : list;
                  if (!useList.length) return '-';
                  const names = useList
                    .map((a: any) => a?.file_name || a?.file || a?.file_path || a?.label)
                    .filter((x: any) => x && String(x).trim());
                  if (!useList.length) return '-';
                  const risks = precheckRiskMap[String(r?.instruction_system_code || '')] || [];
                  const hasAuditFail = risks.includes('运单审核失败');
                  const items = useList.map((a: any) => {
                    const text = String(a?.file_name || a?.file || a?.file_path || a?.label || '').trim() || '附件';
                    const display = text.length > 22 ? `${text.slice(0, 22)}...` : text;
                    return (
                      <div key={String(a?.id ?? text)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {hasAuditFail ? (
                          <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />
                        ) : null}
                        <Tooltip title={text}>
                          <Button type="link" size="small" onClick={() => openAttachmentPreview(a)} style={{ padding: 0 }}>
                            {display}
                          </Button>
                        </Tooltip>
                      </div>
                    );
                  });
                  return <div>{items}</div>;
                },
              },
              {
                title: '指令备注',
                key: 'instruction_remarks',
                width: 220,
                render: (_: any, r: any) => {
                  const text = String(r?.instruction_remarks || '').trim();
                  if (!text) return '-';
                  return (
                    <Tooltip title={text}>
                      <div
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-all',
                        }}
                      >
                        {text}
                      </div>
                    </Tooltip>
                  );
                },
              },
              {
                title: '执行提醒',
                key: 'execution_tips',
                width: 200,
                render: (_: any, r: any) => {
                  const id = String(r?.id ?? '');
                  const text = String(precheckExecutionMap[id] || '').trim();
                  if (!text) return '-';
                  return (
                    <Tooltip title={text}>
                      <div
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-all',
                        }}
                      >
                        {text}
                      </div>
                    </Tooltip>
                  );
                },
              },
              {
                title: '风险项',
                key: 'precheck_risks',
                width: 220,
                render: (_: any, r: any) => {
                  const key = String(r?.instruction_system_code || '');
                  const risks = precheckRiskMap[key] || [];
                  if (!risks.length) return '-';
                  const order = ['物流信息重复', '跟踪号历史重复', '运单审核失败', '其他'];
                  const sorted = [...risks].sort((a, b) => {
                    const ia = order.indexOf(String(a || '').split(':')[0]);
                    const ib = order.indexOf(String(b || '').split(':')[0]);
                    const va = ia === -1 ? 999 : ia;
                    const vb = ib === -1 ? 999 : ib;
                    return va - vb;
                  });
                  return (
                    <div>
                      {sorted.map((risk, idx) => {
                        const raw = String(risk || '');
                        const display = raw.length > 8 ? `${raw.slice(0, 8)}...` : raw;
                        return (
                          <Tooltip key={`${risk}-${idx}`} title={raw}>
                            <div>{display}</div>
                          </Tooltip>
                        );
                      })}
                    </div>
                  );
                },
              },
              {
                title: '预审结果',
                key: 'precheck_result',
                width: 140,
                onCell: (record: any) => ({
                  style: { background: getCarrierBg(record?.waybill_carrier) },
                }),
                render: (_: any, r: any) => {
                  const id = String(r?.id ?? '');
                  const current = precheckResultMap[id];
                  const passActive = current === 'pass';
                  const rejectActive = current === 'reject';
                  return (
                    <Space size={6}>
                      <Button
                        size="small"
                        type="default"
                        style={passActive ? { background: '#52c41a', color: '#fff', borderColor: '#52c41a' } : undefined}
                        onClick={() => {
                          openExecReminder(r);
                        }}
                      >
                        通过
                      </Button>
                      <Button
                        size="small"
                        type="default"
                        style={rejectActive ? { background: '#ff4d4f', color: '#fff', borderColor: '#ff4d4f' } : undefined}
                        onClick={() => {
                          if (String(precheckActive) === 'warehouse') {
                            setRejectReasonOptions([
                              { label: '邮编与地址不匹配', value: '邮编与地址不匹配' },
                              { label: '其他', value: '其他' },
                            ]);
                          } else if (String(precheckActive) === 'upload_after_pack') {
                            setRejectReasonOptions([
                              { label: '无需打包', value: '无需打包' },
                              { label: '未备注特殊包装具体要求', value: '未备注特殊包装具体要求' },
                              { label: '其他', value: '其他' },
                            ]);
                          } else {
                            setRejectReasonOptions([
                              { label: '物流信息重复', value: '物流信息重复' },
                              { label: '跟踪号历史重复', value: '跟踪号历史重复' },
                              { label: '运单审核失败', value: '运单审核失败' },
                              { label: '其他', value: '其他' },
                            ]);
                          }
                          const code = String(r?.instruction_system_code || '');
                          const existing = (precheckRiskMap[code] || []).map((x) => String(x || '').trim()).filter(Boolean);
                          const mapped: string[] = [];
                          let otherText = '';
                          existing.forEach((it) => {
                            if (String(precheckActive) === 'warehouse') {
                              if (it.startsWith('其他:')) {
                                mapped.push('其他');
                                otherText = it.replace(/^其他:/, '').trim();
                              } else if (it === '邮编与地址不匹配') {
                                mapped.push('邮编与地址不匹配');
                              } else {
                                mapped.push('其他');
                                otherText = it;
                              }
                              return;
                            }
                            if (String(precheckActive) === 'upload_after_pack') {
                              if (it.startsWith('其他:')) {
                                mapped.push('其他');
                                otherText = it.replace(/^其他:/, '').trim();
                              } else if (it === '无需打包') {
                                mapped.push('无需打包');
                              } else if (it === '未备注特殊包装具体要求') {
                                mapped.push('未备注特殊包装具体要求');
                              } else {
                                mapped.push('其他');
                                otherText = it;
                              }
                              return;
                            }
                            if (it.startsWith('其他:')) {
                              mapped.push('其他');
                              otherText = it.replace(/^其他:/, '').trim();
                            } else if (it === '重复物流信息') {
                              mapped.push('物流信息重复');
                            } else if (it === '历史重复') {
                              mapped.push('跟踪号历史重复');
                            } else if (it === '运单审核未通过' || it === '运单审核失败') {
                              mapped.push('运单审核失败');
                            } else if (it === '物流信息重复' || it === '跟踪号历史重复' || it === '运单审核失败') {
                              mapped.push(it);
                            } else {
                              mapped.push('其他');
                              otherText = it;
                            }
                          });
                          setRejectTargetIds([id]);
                          setRejectTargetCodes([code]);
                          setRejectReasons(Array.from(new Set(mapped)));
                          setRejectOtherText(otherText);
                          setRejectModalOpen(true);
                        }}
                      >
                        驳回
                      </Button>
                    </Space>
                  );
                },
              },
            ] : precheckActive === 'warehouse' ? [
              {
                title: '指令系统编码',
                dataIndex: 'instruction_system_code',
                key: 'instruction_system_code',
                width: 160,
                render: (v: any) => {
                  const code = v || '-';
                  return (
                    <Space size={6}>
                      <span>{code}</span>
                      {code !== '-' ? (
                        <Tooltip title="复制">
                          <CopyOutlined
                            onClick={async (e) => {
                              e?.stopPropagation?.();
                              try {
                                await navigator.clipboard.writeText(String(code));
                                message.success('已复制');
                              } catch {
                                message.error('复制失败');
                              }
                            }}
                          />
                        </Tooltip>
                      ) : null}
                    </Space>
                  );
                },
              },
              {
                title: '发货产品信息',
                key: 'shipping_products',
                width: 260,
                render: (_: any, r: any) => {
                  const targets = Array.isArray(r?.targets_detail) ? r.targets_detail : [];
                  if (!targets.length) return '-';
                  const seen = new Set<string>();
                  const parts = targets.map((t: any) => {
                    const snap = t?.snapshot || {};
                    const code = t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || snap?.code || t?.inventory_unit_id || '-';
                    const sort = (Array.isArray(t?.sorting_barcodes) && t.sorting_barcodes.length > 0)
                      ? t.sorting_barcodes[0]
                      : (Array.isArray(t?.sample_item_barcodes) && t.sample_item_barcodes.length > 0)
                        ? t.sample_item_barcodes[0]
                        : (Array.isArray(snap?.sorting_barcodes) && snap.sorting_barcodes.length > 0 ? snap.sorting_barcodes[0]?.code : (snap?.sorting_barcode || ''));
                    const qty = Number(t?.expected_qty || 0);
                    const key = `${code}|${sort}|${qty}`;
                    if (seen.has(key)) return null;
                    seen.add(key);
                    const codeText = String(code || '-');
                    const sortText = sort ? `(${sort})` : '';
                    return `${codeText}${sortText}×${isNaN(qty) ? 0 : qty}`;
                  }).filter(Boolean);
                  return (
                    <div>
                      {(parts as string[]).map((line, idx) => (
                        <div key={`${line}-${idx}`}>{line}</div>
                      ))}
                    </div>
                  );
                },
              },
              {
                title: '收件人信息',
                key: 'recipient_info',
                width: 200,
                render: (_: any, r: any) => {
                  const answers = r?.instruction_customer_answers || r?.customer_answers || {};
                  const getVal = (...keys: string[]) => {
                    for (const k of keys) {
                      const v = (answers as any)?.[k];
                      if (v != null && String(v).trim() !== '') return String(v).trim();
                    }
                    return '';
                  };
                  const name = getVal('recipient_name', 'recipient', 'name', 'recipientName');
                  const addr1 = getVal('address_line_1', 'address1', 'address', 'street_1');
                  const addr2 = getVal('address_line_2', 'address2', 'street_2');
                  const city = getVal('city');
                  const state = getVal('state', 'province');
                  const postal = getVal('postal_code', 'zip', 'zipcode');
                  const country = getVal('country');
                  if (!name && !addr1 && !addr2 && !city && !state && !postal && !country) return '-';
                  const line1 = name || '-';
                  const line2 = [addr1, addr2].filter(Boolean).join(' ') || '-';
                  const line3 = [city, state, postal, country].filter(Boolean).join(' ') || '-';
                  const fullText = [line1, line2, line3].join('\n');
                  return (
                    <Tooltip title={fullText}>
                      <div>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{line1}</div>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{line2}</div>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{line3}</div>
                      </div>
                    </Tooltip>
                  );
                },
              },
              {
                title: '需签名服务',
                key: 'need_signature',
                width: 100,
                render: (_: any, r: any) => {
                  const answers = r?.instruction_customer_answers || r?.customer_answers || {};
                  const v = (answers as any)?.signature_required;
                  if (v === true || v === 'true' || v === 1 || v === '1') {
                    return <Tag color="red">是</Tag>;
                  }
                  if (v === false || v === 'false' || v === 0 || v === '0') {
                    return <Tag color="green">否</Tag>;
                  }
                  return '-';
                },
              },
              {
                title: '需保险',
                key: 'need_insurance',
                width: 90,
                render: (_: any, r: any) => {
                  const answers = r?.instruction_customer_answers || r?.customer_answers || {};
                  const v = (answers as any)?.insurance_required;
                  if (v === true || v === 'true' || v === 1 || v === '1') {
                    return <Tag color="red">是</Tag>;
                  }
                  if (v === false || v === 'false' || v === 0 || v === '0') {
                    return <Tag color="green">否</Tag>;
                  }
                  return '-';
                },
              },
              {
                title: '保险金额',
                key: 'insurance_amount',
                width: 100,
                render: (_: any, r: any) => {
                  const answers = r?.instruction_customer_answers || r?.customer_answers || {};
                  const v = (answers as any)?.insured_amount;
                  return v != null && String(v).trim() !== '' ? String(v) : '-';
                },
              },
              {
                title: '偏好物流承运商',
                key: 'preferred_carrier',
                width: 140,
                render: (_: any, r: any) => {
                  const answers = r?.instruction_customer_answers || r?.customer_answers || {};
                  const v = (answers as any)?.preferred_carrier || (answers as any)?.preferred_carrier_code;
                  return v != null && String(v).trim() !== '' ? String(v) : '-';
                },
              },
              {
                title: '买家发货备注',
                key: 'buyer_note',
                width: 160,
                render: (_: any, r: any) => {
                  const answers = r?.instruction_customer_answers || r?.customer_answers || {};
                  const v = (answers as any)?.shipping_notes;
                  return v != null && String(v).trim() !== '' ? String(v) : '-';
                },
              },
              {
                title: '指令备注',
                key: 'instruction_remarks',
                width: 160,
                render: (_: any, r: any) => {
                  const text = String(r?.instruction_remarks || '').trim();
                  if (!text) return '-';
                  return (
                    <Tooltip title={text}>
                      <div
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-all',
                        }}
                      >
                        {text}
                      </div>
                    </Tooltip>
                  );
                },
              },
              {
                title: '执行提醒',
                key: 'execution_tips',
                width: 200,
                render: (_: any, r: any) => {
                  const id = String(r?.id ?? '');
                  const text = String(precheckExecutionMap[id] || '').trim();
                  if (!text) return '-';
                  return (
                    <Tooltip title={text}>
                      <div
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-all',
                        }}
                      >
                        {text}
                      </div>
                    </Tooltip>
                  );
                },
              },
              {
                title: '风险项',
                key: 'precheck_risks',
                width: 200,
                render: (_: any, r: any) => {
                  const key = String(r?.instruction_system_code || '');
                  const risks = precheckRiskMap[key] || [];
                  if (!risks.length) return '-';
                  const order = ['物流信息重复', '跟踪号历史重复', '运单审核失败', '其他'];
                  const sorted = [...risks].sort((a, b) => {
                    const ia = order.indexOf(String(a || '').split(':')[0]);
                    const ib = order.indexOf(String(b || '').split(':')[0]);
                    const va = ia === -1 ? 999 : ia;
                    const vb = ib === -1 ? 999 : ib;
                    return va - vb;
                  });
                  return (
                    <div>
                      {sorted.map((risk, idx) => {
                        const raw = String(risk || '');
                        const display = raw.length > 8 ? `${raw.slice(0, 8)}...` : raw;
                        return (
                          <Tooltip key={`${risk}-${idx}`} title={raw}>
                            <div>{display}</div>
                          </Tooltip>
                        );
                      })}
                    </div>
                  );
                },
              },
              {
                title: '预审结果',
                key: 'precheck_result',
                width: 140,
                fixed: 'right' as const,
                onCell: (record: any) => ({
                  style: { background: getCarrierBg(record?.waybill_carrier) },
                }),
                render: (_: any, r: any) => {
                  const id = String(r?.id ?? '');
                  const current = precheckResultMap[id];
                  const passActive = current === 'pass';
                  const rejectActive = current === 'reject';
                  return (
                    <Space size={6}>
                      <Button
                        size="small"
                        type="default"
                        style={passActive ? { background: '#52c41a', color: '#fff', borderColor: '#52c41a' } : undefined}
                        onClick={() => {
                          openExecReminder(r);
                        }}
                      >
                        通过
                      </Button>
                      <Button
                        size="small"
                        type="default"
                        style={rejectActive ? { background: '#ff4d4f', color: '#fff', borderColor: '#ff4d4f' } : undefined}
                        onClick={() => {
                          if (String(precheckActive) === 'warehouse') {
                            setRejectReasonOptions([
                              { label: '邮编与地址不匹配', value: '邮编与地址不匹配' },
                              { label: '其他', value: '其他' },
                            ]);
                          } else if (String(precheckActive) === 'upload_after_pack') {
                            setRejectReasonOptions([
                              { label: '无需打包', value: '无需打包' },
                              { label: '未备注特殊包装具体要求', value: '未备注特殊包装具体要求' },
                              { label: '其他', value: '其他' },
                            ]);
                          } else {
                            setRejectReasonOptions([
                              { label: '物流信息重复', value: '物流信息重复' },
                              { label: '跟踪号历史重复', value: '跟踪号历史重复' },
                              { label: '运单审核失败', value: '运单审核失败' },
                              { label: '其他', value: '其他' },
                            ]);
                          }
                          const code = String(r?.instruction_system_code || '');
                          const existing = (precheckRiskMap[code] || []).map((x) => String(x || '').trim()).filter(Boolean);
                          const mapped: string[] = [];
                          let otherText = '';
                          existing.forEach((it) => {
                            if (String(precheckActive) === 'warehouse') {
                              if (it.startsWith('其他:')) {
                                mapped.push('其他');
                                otherText = it.replace(/^其他:/, '').trim();
                              } else if (it === '邮编与地址不匹配') {
                                mapped.push('邮编与地址不匹配');
                              } else {
                                mapped.push('其他');
                                otherText = it;
                              }
                              return;
                            }
                            if (String(precheckActive) === 'upload_after_pack') {
                              if (it.startsWith('其他:')) {
                                mapped.push('其他');
                                otherText = it.replace(/^其他:/, '').trim();
                              } else if (it === '无需打包') {
                                mapped.push('无需打包');
                              } else if (it === '未备注特殊包装具体要求') {
                                mapped.push('未备注特殊包装具体要求');
                              } else {
                                mapped.push('其他');
                                otherText = it;
                              }
                              return;
                            }
                            if (it.startsWith('其他:')) {
                              mapped.push('其他');
                              otherText = it.replace(/^其他:/, '').trim();
                            } else if (it === '重复物流信息') {
                              mapped.push('物流信息重复');
                            } else if (it === '历史重复') {
                              mapped.push('跟踪号历史重复');
                            } else if (it === '运单审核未通过' || it === '运单审核失败') {
                              mapped.push('运单审核失败');
                            } else if (it === '物流信息重复' || it === '跟踪号历史重复' || it === '运单审核失败') {
                              mapped.push(it);
                            } else {
                              mapped.push('其他');
                              otherText = it;
                            }
                          });
                          setRejectTargetIds([id]);
                          setRejectTargetCodes([code]);
                          setRejectReasons(Array.from(new Set(mapped)));
                          setRejectOtherText(otherText);
                          setRejectModalOpen(true);
                        }}
                      >
                        驳回
                      </Button>
                    </Space>
                  );
                },
              },
            ] : precheckActive === 'upload_after_pack' ? [
              {
                title: '指令系统编码',
                dataIndex: 'instruction_system_code',
                key: 'instruction_system_code',
                width: 160,
                render: (v: any) => {
                  const code = v || '-';
                  return (
                    <Space size={6}>
                      <span>{code}</span>
                      {code !== '-' ? (
                        <Tooltip title="复制">
                          <CopyOutlined
                            onClick={async (e) => {
                              e?.stopPropagation?.();
                              try {
                                await navigator.clipboard.writeText(String(code));
                                message.success('已复制');
                              } catch {
                                message.error('复制失败');
                              }
                            }}
                          />
                        </Tooltip>
                      ) : null}
                    </Space>
                  );
                },
              },
              {
                title: '发货产品信息',
                key: 'shipping_products',
                width: 260,
                render: (_: any, r: any) => {
                  const targets = Array.isArray(r?.targets_detail) ? r.targets_detail : [];
                  if (!targets.length) return '-';
                  const seen = new Set<string>();
                  const parts = targets.map((t: any) => {
                    const snap = t?.snapshot || {};
                    const code = t?.inventory_unit_system_barcode || snap?.system_barcode || snap?.system_code || snap?.code || t?.inventory_unit_id || '-';
                    const sort = (Array.isArray(t?.sorting_barcodes) && t.sorting_barcodes.length > 0)
                      ? t.sorting_barcodes[0]
                      : (Array.isArray(t?.sample_item_barcodes) && t.sample_item_barcodes.length > 0)
                        ? t.sample_item_barcodes[0]
                        : (Array.isArray(snap?.sorting_barcodes) && snap.sorting_barcodes.length > 0 ? snap.sorting_barcodes[0]?.code : (snap?.sorting_barcode || ''));
                    const qty = Number(t?.expected_qty || 0);
                    const key = `${code}|${sort}|${qty}`;
                    if (seen.has(key)) return null;
                    seen.add(key);
                    const codeText = String(code || '-');
                    const sortText = sort ? `(${sort})` : '';
                    return `${codeText}${sortText}×${isNaN(qty) ? 0 : qty}`;
                  }).filter(Boolean);
                  return (
                    <div>
                      {(parts as string[]).map((line, idx) => (
                        <div key={`${line}-${idx}`}>{line}</div>
                      ))}
                    </div>
                  );
                },
              },
              {
                title: '是否需打包',
                key: 'is_pack_required',
                width: 110,
                render: (_: any, r: any) => {
                  if (r?.is_pack_required === true) return <Tag color="red">是</Tag>;
                  if (r?.is_pack_required === false) return <Tag color="green">否</Tag>;
                  return '-';
                },
              },
              {
                title: '包装偏好',
                key: 'estimated_packing_method',
                width: 120,
                render: (_: any, r: any) => {
                  const m = String(r?.estimated_packing_method || '');
                  if (m === 'warehouse_free') return '仓库免费包材';
                  if (m === 'warehouse_paid') return '仓库收费包材';
                  if (m === 'customer_carton') return '客户自购包材(纸箱)';
                  if (m === 'customer_non_carton') return '客户自购包材(非纸箱)';
                  if (m === 'special_packing') return '特殊包装';
                  return m || '-';
                },
              },
              {
                title: '指令备注',
                key: 'instruction_remarks',
                width: 160,
                render: (_: any, r: any) => {
                  const text = String(r?.instruction_remarks || '').trim();
                  if (!text) return '-';
                  return (
                    <Tooltip title={text}>
                      <div
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-all',
                        }}
                      >
                        {text}
                      </div>
                    </Tooltip>
                  );
                },
              },
              {
                title: '执行提醒',
                key: 'execution_tips',
                width: 200,
                render: (_: any, r: any) => {
                  const id = String(r?.id ?? '');
                  const text = String(precheckExecutionMap[id] || '').trim();
                  if (!text) return '-';
                  return (
                    <Tooltip title={text}>
                      <div
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-all',
                        }}
                      >
                        {text}
                      </div>
                    </Tooltip>
                  );
                },
              },
              {
                title: '风险项',
                key: 'precheck_risks',
                width: 240,
                render: (_: any, r: any) => {
                  const key = String(r?.instruction_system_code || '');
                  const risks = precheckRiskMap[key] || [];
                  if (!risks.length) return '-';
                  const order = ['物流信息重复', '跟踪号历史重复', '运单审核失败', '其他', '邮编与地址不匹配'];
                  const sorted = [...risks].sort((a, b) => {
                    const ia = order.indexOf(String(a || '').split(':')[0]);
                    const ib = order.indexOf(String(b || '').split(':')[0]);
                    const va = ia === -1 ? 999 : ia;
                    const vb = ib === -1 ? 999 : ib;
                    return va - vb;
                  });
                  return (
                    <div>
                      {sorted.map((risk, idx) => {
                        const raw = String(risk || '');
                        const display = raw.length > 8 ? `${raw.slice(0, 8)}...` : raw;
                        return (
                          <Tooltip key={`${risk}-${idx}`} title={raw}>
                            <div>{display}</div>
                          </Tooltip>
                        );
                      })}
                    </div>
                  );
                },
              },
              {
                title: '预审结果',
                key: 'precheck_result',
                width: 140,
                render: (_: any, r: any) => {
                  const id = String(r?.id ?? '');
                  const current = precheckResultMap[id];
                  const passActive = current === 'pass';
                  const rejectActive = current === 'reject';
                  return (
                    <Space size={6}>
                      <Button
                        size="small"
                        type="default"
                        style={passActive ? { background: '#52c41a', color: '#fff', borderColor: '#52c41a' } : undefined}
                        onClick={() => {
                          openExecReminder(r);
                        }}
                      >
                        通过
                      </Button>
                      <Button
                        size="small"
                        type="default"
                        style={rejectActive ? { background: '#ff4d4f', color: '#fff', borderColor: '#ff4d4f' } : undefined}
                        onClick={() => {
                          if (String(precheckActive) === 'warehouse') {
                            setRejectReasonOptions([
                              { label: '邮编与地址不匹配', value: '邮编与地址不匹配' },
                              { label: '其他', value: '其他' },
                            ]);
                          } else if (String(precheckActive) === 'upload_after_pack') {
                            setRejectReasonOptions([
                              { label: '无需打包', value: '无需打包' },
                              { label: '未备注特殊包装具体要求', value: '未备注特殊包装具体要求' },
                              { label: '其他', value: '其他' },
                            ]);
                          } else {
                            setRejectReasonOptions([
                              { label: '物流信息重复', value: '物流信息重复' },
                              { label: '跟踪号历史重复', value: '跟踪号历史重复' },
                              { label: '运单审核失败', value: '运单审核失败' },
                              { label: '其他', value: '其他' },
                            ]);
                          }
                          const code = String(r?.instruction_system_code || '');
                          const existing = (precheckRiskMap[code] || []).map((x) => String(x || '').trim()).filter(Boolean);
                          const mapped: string[] = [];
                          let otherText = '';
                          existing.forEach((it) => {
                            if (String(precheckActive) === 'warehouse') {
                              if (it.startsWith('其他:')) {
                                mapped.push('其他');
                                otherText = it.replace(/^其他:/, '').trim();
                              } else if (it === '邮编与地址不匹配') {
                                mapped.push('邮编与地址不匹配');
                              } else {
                                mapped.push('其他');
                                otherText = it;
                              }
                              return;
                            }
                            if (String(precheckActive) === 'upload_after_pack') {
                              if (it.startsWith('其他:')) {
                                mapped.push('其他');
                                otherText = it.replace(/^其他:/, '').trim();
                              } else if (it === '无需打包') {
                                mapped.push('无需打包');
                              } else if (it === '未备注特殊包装具体要求') {
                                mapped.push('未备注特殊包装具体要求');
                              } else {
                                mapped.push('其他');
                                otherText = it;
                              }
                              return;
                            }
                            if (it.startsWith('其他:')) {
                              mapped.push('其他');
                              otherText = it.replace(/^其他:/, '').trim();
                            } else if (it === '重复物流信息') {
                              mapped.push('物流信息重复');
                            } else if (it === '历史重复') {
                              mapped.push('跟踪号历史重复');
                            } else if (it === '运单审核未通过' || it === '运单审核失败') {
                              mapped.push('运单审核失败');
                            } else if (it === '物流信息重复' || it === '跟踪号历史重复' || it === '运单审核失败') {
                              mapped.push(it);
                            } else {
                              mapped.push('其他');
                              otherText = it;
                            }
                          });
                          setRejectTargetIds([id]);
                          setRejectTargetCodes([code]);
                          setRejectReasons(Array.from(new Set(mapped)));
                          setRejectOtherText(otherText);
                          setRejectModalOpen(true);
                        }}
                      >
                        驳回
                      </Button>
                    </Space>
                  );
                },
              },
            ] : [
              {
                title: '指令系统编码',
                dataIndex: 'instruction_system_code',
                key: 'instruction_system_code',
                width: 180,
                render: (v: any) => {
                  const code = v || '-';
                  return (
                    <Space size={6}>
                      <span>{code}</span>
                      {code !== '-' ? (
                        <Tooltip title="复制">
                          <CopyOutlined
                            onClick={async (e) => {
                              e?.stopPropagation?.();
                              try {
                                await navigator.clipboard.writeText(String(code));
                                message.success('已复制');
                              } catch {
                                message.error('复制失败');
                              }
                            }}
                          />
                        </Tooltip>
                      ) : null}
                    </Space>
                  );
                },
              },
              { title: '指令状态', dataIndex: 'instruction_status', key: 'instruction_status', width: 120 },
              {
                title: '预审结果',
                key: 'precheck_result',
                width: 140,
                onCell: (record: any) => ({
                  style: { background: getCarrierBg(record?.waybill_carrier) },
                }),
                render: (_: any, r: any) => {
                  const id = String(r?.id ?? '');
                  const current = precheckResultMap[id];
                  const passActive = current === 'pass';
                  const rejectActive = current === 'reject';
                  return (
                    <Space size={6}>
                      <Button
                        size="small"
                        type="default"
                        style={passActive ? { background: '#52c41a', color: '#fff', borderColor: '#52c41a' } : undefined}
                        onClick={() => {
                          setPrecheckResultMap((prev) => ({ ...prev, [id]: 'pass' }));
                          const codeKey = String(r?.instruction_system_code || '');
                          if (codeKey) {
                            setPrecheckRiskMap((prev) => {
                              const next = { ...prev };
                              delete next[codeKey];
                              return next;
                            });
                          }
                        }}
                      >
                        通过
                      </Button>
                      <Button
                        size="small"
                        type="default"
                        style={rejectActive ? { background: '#ff4d4f', color: '#fff', borderColor: '#ff4d4f' } : undefined}
                        onClick={() => {
                          const code = String(r?.instruction_system_code || '');
                          const existing = (precheckRiskMap[code] || []).map((x) => String(x || '').trim()).filter(Boolean);
                          const mapped: string[] = [];
                          let otherText = '';
                          existing.forEach((it) => {
                            if (it.startsWith('其他:')) {
                              mapped.push('其他');
                              otherText = it.replace(/^其他:/, '').trim();
                            } else if (it === '重复物流信息') {
                              mapped.push('物流信息重复');
                            } else if (it === '历史重复') {
                              mapped.push('跟踪号历史重复');
                            } else if (it === '运单审核未通过' || it === '运单审核失败') {
                              mapped.push('运单审核失败');
                            } else if (it === '物流信息重复' || it === '跟踪号历史重复' || it === '运单审核失败') {
                              mapped.push(it);
                            } else {
                              mapped.push('其他');
                              otherText = it;
                            }
                          });
                          setRejectTargetIds([id]);
                          setRejectTargetCodes([code]);
                          setRejectReasons(Array.from(new Set(mapped)));
                          setRejectOtherText(otherText);
                          setRejectModalOpen(true);
                        }}
                      >
                        驳回
                      </Button>
                    </Space>
                  );
                },
              },
            ]}
          />
        </Modal>
        <Modal
          open={rejectModalOpen}
          title="选择驳回理由"
          okText="确认"
          cancelText="取消"
          onCancel={() => setRejectModalOpen(false)}
          onOk={() => {
            if (!rejectTargetIds.length) return;
            if (rejectReasons.length === 0) {
              message.warning('请至少选择一个驳回理由');
              return;
            }
            if (rejectReasons.includes('其他') && !String(rejectOtherText || '').trim()) {
              message.warning('请填写其他理由备注');
              return;
            }
            const reasons = rejectReasons.map((r) => (r === '其他' ? `其他:${String(rejectOtherText || '').trim()}` : r));
            setPrecheckResultMap((prev) => {
              const next = { ...prev };
              rejectTargetIds.forEach((id) => { next[id] = 'reject'; });
              return next;
            });
            setPrecheckExecutionMap((prev) => {
              const next = { ...prev };
              rejectTargetIds.forEach((id) => { delete next[id]; });
              return next;
            });
            setPrecheckRiskMap((prev) => {
              const next = { ...prev };
              const keys = rejectTargetCodes.length ? rejectTargetCodes : rejectTargetIds;
              keys.forEach((key) => { if (key) next[key] = reasons; });
              return next;
            });
            setRejectModalOpen(false);
          }}
        >
          <Checkbox.Group
            value={rejectReasons}
            onChange={(vals) => setRejectReasons(vals as string[])}
          >
            <Space direction="vertical">
              {rejectReasonOptions.map((opt) => (
                <Checkbox key={opt.value} value={opt.value}>{opt.label}</Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
          {rejectReasons.includes('其他') ? (
            <Input.TextArea
              value={rejectOtherText}
              onChange={(e) => setRejectOtherText(e.target.value)}
              placeholder="请输入备注"
              style={{ marginTop: 12 }}
              rows={3}
            />
          ) : null}
        </Modal>
        <Modal
          open={execReminderOpen}
          title={(
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>执行提醒</span>
              <span style={{ color: '#8c8c8c', fontSize: 12 }}>为仓库执行端添加操作时的提醒信息</span>
            </div>
          )}
          okText="确认"
          cancelText="取消"
          onCancel={() => setExecReminderOpen(false)}
          onOk={confirmExecReminder}
        >
          <div style={{ marginBottom: 8 }}>
            {execReminderTargetIds.length > 1
              ? `已选择 ${execReminderTargetIds.length} 条记录`
              : `当前指令：${execReminderTargetCodes[0] || execReminderTargetIds[0] || '-'}`}
          </div>
          <Input.TextArea
            value={execReminderText}
            onChange={(e) => setExecReminderText(e.target.value)}
            placeholder="请输入执行提醒（可留空）"
            rows={3}
          />
        </Modal>
        <Modal
          open={completePrecheckOpen}
          title="预审执行中"
          footer={null}
          closable={false}
        >
          <div style={{ marginBottom: 12 }}>
            {`已完成 ${completePrecheckProgress.done} / ${completePrecheckProgress.total}`}
          </div>
          <Progress
            percent={completePrecheckProgress.total > 0
              ? Math.round((completePrecheckProgress.done / completePrecheckProgress.total) * 100)
              : 0}
          />
        </Modal>
        <Modal
          open={completePrecheckErrorsOpen}
          title="预审失败明细"
          okText="关闭"
          cancelButtonProps={{ style: { display: 'none' } }}
          onCancel={() => setCompletePrecheckErrorsOpen(false)}
          onOk={() => setCompletePrecheckErrorsOpen(false)}
        >
          <div>
            {completePrecheckErrors.length === 0 ? (
              <div>暂无失败记录</div>
            ) : (
              <ul style={{ paddingLeft: 18, margin: 0 }}>
                {completePrecheckErrors.map((item, idx) => (
                  <li key={`${item}-${idx}`}>{item}</li>
                ))}
              </ul>
            )}
          </div>
        </Modal>
        <Modal
          open={attPreviewOpen}
          title={attPreviewTitle || '附件预览'}
          footer={null}
          width={900}
          onCancel={() => {
            setAttPreviewOpen(false);
            setAttPreviewUrl('');
            if (attPreviewObjectUrlRef.current) {
              try { URL.revokeObjectURL(attPreviewObjectUrlRef.current); } catch {}
              attPreviewObjectUrlRef.current = null;
            }
          }}
        >
          {attPreviewLoading ? (
            <div style={{ padding: 24 }}>加载中...</div>
          ) : attPreviewKind === 'image' ? (
            <img src={attPreviewUrl} alt="preview" style={{ maxWidth: '100%' }} />
          ) : (
            <iframe title="preview" src={attPreviewUrl} style={{ width: '100%', height: 600, border: 'none' }} />
          )}
        </Modal>
        <Modal
          open={autoPrecheckOpen}
          title={(
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', paddingRight: 32 }}>
              <span>自动预审结果汇总</span>
              <Button
                danger
                disabled={!autoPrecheckRunning}
                onClick={() => {
                  autoPrecheckCancelRef.current = true;
                  setAutoPrecheckRunning(false);
                  setAutoPrecheckOpen(false);
                }}
              >
                终止预审
              </Button>
              <div style={{ justifySelf: 'end' }}>
              <Button
                size="small"
                onClick={async () => {
                  try {
                    const lines: string[] = [];
                    if (autoPrecheckMode === 'warehouse') {
                      const warningCount = autoPrecheckWarehouseMismatches.length;
                      const passedCount = Math.max(autoPrecheckSummary.total - warningCount, 0);
                      lines.push(`基础统计`);
                      lines.push(`总记录数: ${autoPrecheckSummary.total}`);
                      lines.push(`警告与审核未通过: ${warningCount}`);
                      lines.push(`已通过: ${passedCount}`);
                      lines.push(`风险项数量汇总`);
                      lines.push(`邮编与地址不匹配: ${autoPrecheckWarehouseMismatches.length}`);
                      if (autoPrecheckWarehouseMismatches.length > 0) {
                        lines.push('');
                        lines.push('邮编与地址不匹配');
                        autoPrecheckWarehouseMismatches.forEach((m) => {
                          lines.push(`指令编号: ${String(m.code || '-').toUpperCase()}`);
                          lines.push(`输入: ${String(m.inputCity || '-').toUpperCase()} | ${String(m.inputState || '-').toUpperCase()} | ${String(m.inputZip || '-').toUpperCase()}`);
                          lines.push(`邮编匹配: ${String(m.zipCity || '-').toUpperCase()} | ${String(m.zipState || '-').toUpperCase()} | ${String(m.inputZip || '-').toUpperCase()}`);
                        });
                      }
                    } else if (autoPrecheckMode === 'upload_after_pack') {
                      const warnSet = new Set<string>([
                        ...autoPrecheckAfterPackWarnings.noPack,
                        ...autoPrecheckAfterPackWarnings.specialNoRemark,
                      ].filter(Boolean));
                      const warningCount = warnSet.size;
                      const passedCount = Math.max(autoPrecheckSummary.total - warningCount, 0);
                      lines.push(`基础统计`);
                      lines.push(`总记录数: ${autoPrecheckSummary.total}`);
                      lines.push(`警告与审核未通过: ${warningCount}`);
                      lines.push(`已通过: ${passedCount}`);
                      lines.push(`风险项数量汇总`);
                      lines.push(`无需打包: ${autoPrecheckAfterPackWarnings.noPack.length}`);
                      lines.push(`未备注特殊包装具体要求: ${autoPrecheckAfterPackWarnings.specialNoRemark.length}`);
                      if (autoPrecheckAfterPackWarnings.noPack.length > 0) {
                        lines.push('');
                        lines.push('无需打包');
                        autoPrecheckAfterPackWarnings.noPack.forEach((code) => {
                          lines.push(`${code || '-'}`);
                        });
                      }
                      if (autoPrecheckAfterPackWarnings.specialNoRemark.length > 0) {
                        lines.push('');
                        lines.push('未备注特殊包装具体要求');
                        autoPrecheckAfterPackWarnings.specialNoRemark.forEach((code) => {
                          lines.push(`${code || '-'}`);
                        });
                      }
                    } else {
                      const warningSerialSet = new Set<string>();
                      autoPrecheckSummary.duplicatePairs.forEach((it) => {
                        (it.serials || []).forEach((s) => s && warningSerialSet.add(String(s)));
                      });
                      autoPrecheckSummary.historyWarnings.forEach((it) => {
                        if (it.serial) warningSerialSet.add(String(it.serial));
                      });
                      autoPrecheckSummary.auditIssues.forEach((it) => {
                        if (it.serial) warningSerialSet.add(String(it.serial));
                      });
                      const warningCount = warningSerialSet.size;
                      const passedCount = Math.max(autoPrecheckSummary.total - warningCount, 0);
                      lines.push(`基础统计`);
                      lines.push(`总记录数: ${autoPrecheckSummary.total}`);
                      lines.push(`警告与审核未通过: ${warningCount}`);
                      lines.push(`已通过: ${passedCount}`);
                      lines.push(`风险项数量汇总`);
                      lines.push(`重复物流信息: ${autoPrecheckSummary.duplicatePairs.length}`);
                      lines.push(`历史物流信息: ${autoPrecheckSummary.historyWarnings.length}`);
                      lines.push(`运单审核失败: ${autoPrecheckSummary.auditIssues.length}`);
                      if (autoPrecheckSummary.duplicatePairs.length > 0) {
                        lines.push('');
                        lines.push('存在重复物流信息');
                        autoPrecheckSummary.duplicatePairs.forEach((it) => {
                          lines.push(`${it.carrier || '-'} ${it.tracking || '-'} ×${it.count}`);
                        });
                      }
                      if (autoPrecheckSummary.historyWarnings.length > 0) {
                        lines.push('');
                        lines.push('存在已使用的历史物流信息');
                        autoPrecheckSummary.historyWarnings.forEach((it) => {
                          const info = (it.items || []).map((x) => `${x.provider || '-'} ${x.tracking_number || '-'}`).join('；');
                          lines.push(`${it.serial || '-'}: ${info || '-'}`);
                        });
                      }
                      if (autoPrecheckSummary.auditIssues.length > 0) {
                        lines.push('');
                        lines.push('运单审核失败');
                        autoPrecheckSummary.auditIssues.forEach((it) => {
                          const msg = `${it.label || ''}${it.label ? ':' : ''}${it.message || ''}` || '-';
                          lines.push(`${it.serial || '-'} | ${it.fileName || '-'} | ${msg}`);
                        });
                      }
                    }
                    await navigator.clipboard.writeText(lines.join('\n'));
                    message.success('已复制');
                  } catch {
                    message.error('复制失败');
                  }
                }}
              >
                复制
              </Button>
              </div>
            </div>
          )}
          footer={null}
          width={960}
          onCancel={() => setAutoPrecheckOpen(false)}
        >
          {autoPrecheckProgress.total > 0 ? (
            <div style={{ marginBottom: 12 }}>
              <Progress
                percent={Math.round((autoPrecheckProgress.done / autoPrecheckProgress.total) * 100)}
                status={autoPrecheckRunning ? 'active' : 'success'}
              />
              <div style={{ color: '#888' }}>
                {`已完成 ${autoPrecheckProgress.done} / ${autoPrecheckProgress.total}`}
              </div>
            </div>
          ) : null}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>基础统计</div>
            {(() => {
              const total = autoPrecheckSummary.total;
              let failCount = 0;
              if (autoPrecheckMode === 'warehouse') {
                failCount = autoPrecheckWarehouseMismatches.length;
              } else if (autoPrecheckMode === 'upload_after_pack') {
                const warnSet = new Set<string>([
                  ...autoPrecheckAfterPackWarnings.noPack,
                  ...autoPrecheckAfterPackWarnings.specialNoRemark,
                ].filter(Boolean));
                failCount = warnSet.size;
              } else {
                const warningSerialSet = new Set<string>();
                autoPrecheckSummary.duplicatePairs.forEach((it) => {
                  (it.serials || []).forEach((s) => s && warningSerialSet.add(String(s)));
                });
                autoPrecheckSummary.historyWarnings.forEach((it) => {
                  if (it.serial) warningSerialSet.add(String(it.serial));
                });
                autoPrecheckSummary.auditIssues.forEach((it) => {
                  if (it.serial) warningSerialSet.add(String(it.serial));
                });
                failCount = warningSerialSet.size;
              }
              const passedCount = Math.max(total - failCount, 0);
              return (
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <span>{`总记录数:${total}`}</span>
                  <span>{`已通过:${passedCount}`}</span>
                  <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{`未通过:${failCount}`}</span>
                </div>
              );
            })()}
          </div>
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              type="primary"
              disabled={!autoPrecheckFinished}
              onClick={() => {
                if (autoPrecheckMode === 'warehouse') {
                  applyAllRisksAndCloseWarehouse();
                  return;
                }
                if (autoPrecheckMode === 'upload_after_pack') {
                  applyAllRisksAndCloseAfterPack();
                  return;
                }
                applyAllRisksAndClose();
              }}
            >
              标记预审结果并关闭
            </Button>
            <Button
              disabled={!autoPrecheckFinished}
              onClick={() => {
                Modal.confirm({
                  title: '是否标记全部风险项?',
                  okText: '是',
                  cancelText: '否',
                  onOk: () => {
                    if (autoPrecheckMode === 'warehouse') {
                      applyAllRisksAndCloseWarehouse();
                      return;
                    }
                    if (autoPrecheckMode === 'upload_after_pack') {
                      applyAllRisksAndCloseAfterPack();
                      return;
                    }
                    applyAllRisksAndClose();
                  },
                  onCancel: () => setAutoPrecheckOpen(false),
                });
              }}
            >
              仅关闭
            </Button>
          </div>
          {autoPrecheckMode === 'warehouse' ? (
            autoPrecheckWarehouseMismatches.length > 0 ? (
              <div style={{ marginTop: 12, background: '#fffbe6', border: '1px solid #ffe58f', color: '#000', padding: '10px 12px', borderRadius: 4 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>{`邮编与地址不匹配 ${autoPrecheckWarehouseMismatches.length}条`}</div>
                <div style={{ maxHeight: 320, overflow: 'auto' }}>
                  {autoPrecheckWarehouseMismatches.map((m, idx) => (
                    <div key={`${m.code}-${idx}`} style={{ marginBottom: 8 }}>
                      <div>{`指令编号: ${String(m.code || '-').toUpperCase()}`}</div>
                      <div>{`输入: ${String(m.inputCity || '-').toUpperCase()} | ${String(m.inputState || '-').toUpperCase()} | ${String(m.inputZip || '-').toUpperCase()}`}</div>
                      <div>{`邮编匹配: ${String(m.zipCity || '-').toUpperCase()} | ${String(m.zipState || '-').toUpperCase()} | ${String(m.inputZip || '-').toUpperCase()}`}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ padding: '8px 0' }}>未发现异常。</div>
            )
          ) : autoPrecheckMode === 'upload_after_pack' ? (
            (autoPrecheckAfterPackWarnings.noPack.length > 0 || autoPrecheckAfterPackWarnings.specialNoRemark.length > 0) ? (
              <div style={{ marginTop: 12, background: '#fffbe6', border: '1px solid #ffe58f', color: '#000', padding: '10px 12px', borderRadius: 4 }}>
                {autoPrecheckAfterPackWarnings.noPack.length > 0 ? (
                  <div style={{ marginBottom: autoPrecheckAfterPackWarnings.specialNoRemark.length > 0 ? 10 : 0 }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>{`无需打包 ${autoPrecheckAfterPackWarnings.noPack.length}条`}</div>
                    <div style={{ maxHeight: 240, overflow: 'auto' }}>
                      {autoPrecheckAfterPackWarnings.noPack.map((code, idx) => (
                        <div key={`${code}-${idx}`}>{code || '-'}</div>
                      ))}
                    </div>
                  </div>
                ) : null}
                {autoPrecheckAfterPackWarnings.specialNoRemark.length > 0 ? (
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>{`未备注特殊包装具体要求 ${autoPrecheckAfterPackWarnings.specialNoRemark.length}条`}</div>
                    <div style={{ maxHeight: 240, overflow: 'auto' }}>
                      {autoPrecheckAfterPackWarnings.specialNoRemark.map((code, idx) => (
                        <div key={`${code}-${idx}`}>{code || '-'}</div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <div style={{ padding: '8px 0' }}>未发现异常。</div>
            )
          ) : (
            (autoPrecheckSummary.duplicatePairs.length > 0 || autoPrecheckSummary.historyWarnings.length > 0 || autoPrecheckSummary.auditIssues.length > 0) ? (
            <div style={{ marginTop: 12, background: '#fffbe6', border: '1px solid #ffe58f', color: '#000', padding: '10px 12px', borderRadius: 4 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>预审警告:</div>
              {autoPrecheckSummary.duplicatePairs.length > 0 ? (
                <div style={{ marginBottom: (autoPrecheckSummary.historyWarnings.length > 0 || autoPrecheckSummary.auditIssues.length > 0) ? 10 : 0 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>
                    {`存在重复物流信息 ${autoPrecheckSummary.duplicatePairs.length}条`}
                    <span style={{ display: 'inline-block', marginLeft: 8, padding: '2px 8px', border: '1px solid #000', borderRadius: 4, fontWeight: 400 }}>
                      可继续提交,但可能影响以后查单
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 120px', border: '1px solid #ffe58f', background: '#fffbe6' }}>
                    <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', fontWeight: 600 }}>物流服务商</div>
                    <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', fontWeight: 600 }}>物流跟踪号</div>
                    <div style={{ padding: '6px 8px', fontWeight: 600 }}>出现次数</div>
                    {autoPrecheckSummary.duplicatePairs
                      .slice((autoPrecheckDupPage - 1) * autoPrecheckDupPageSize, autoPrecheckDupPage * autoPrecheckDupPageSize)
                      .map((item) => (
                      <React.Fragment key={`${item.carrier}-${item.tracking}`}>
                        <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', borderTop: '1px solid #ffe58f' }}>{item.carrier || '-'}</div>
                        <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', borderTop: '1px solid #ffe58f' }}>{item.tracking || '-'}</div>
                        <div style={{ padding: '6px 8px', borderTop: '1px solid #ffe58f' }}>{item.count}</div>
                      </React.Fragment>
                    ))}
                  </div>
                  <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                    <Pagination
                      size="small"
                      current={autoPrecheckDupPage}
                      pageSize={autoPrecheckDupPageSize}
                      total={autoPrecheckSummary.duplicatePairs.length}
                      showSizeChanger
                      pageSizeOptions={[5, 10, 20, 50] as any}
                      onChange={(page, pageSize) => {
                        setAutoPrecheckDupPage(page);
                        if (pageSize) setAutoPrecheckDupPageSize(pageSize);
                      }}
                    />
                  </div>
                </div>
              ) : null}
              {autoPrecheckSummary.historyWarnings.length > 0 ? (
                <div style={{ marginBottom: autoPrecheckSummary.auditIssues.length > 0 ? 10 : 0 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>
                    {`存在已使用的历史物流信息 ${autoPrecheckSummary.historyWarnings.length}条`}
                    <span style={{ display: 'inline-block', marginLeft: 8, padding: '2px 8px', border: '1px solid #000', borderRadius: 4, fontWeight: 400 }}>
                      可继续提交,但可能影响以后查单
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', border: '1px solid #ffe58f', background: '#fffbe6' }}>
                    <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', fontWeight: 600 }}>指令编号</div>
                    <div style={{ padding: '6px 8px', fontWeight: 600 }}>物流信息</div>
                    {autoPrecheckSummary.historyWarnings
                      .slice((autoPrecheckHistoryPage - 1) * autoPrecheckHistoryPageSize, autoPrecheckHistoryPage * autoPrecheckHistoryPageSize)
                      .map((item, idx) => (
                      <React.Fragment key={`${item.serial}-${idx}`}>
                        <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', borderTop: '1px solid #ffe58f' }}>{item.serial || '-'}</div>
                        <div style={{ padding: '6px 8px', borderTop: '1px solid #ffe58f' }}>
                          {(item.items || []).map((it) => `${it.provider || '-'} ${it.tracking_number || '-'}`).join('；') || '-'}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                  <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                    <Pagination
                      size="small"
                      current={autoPrecheckHistoryPage}
                      pageSize={autoPrecheckHistoryPageSize}
                      total={autoPrecheckSummary.historyWarnings.length}
                      showSizeChanger
                      pageSizeOptions={[5, 10, 20, 50] as any}
                      onChange={(page, pageSize) => {
                        setAutoPrecheckHistoryPage(page);
                        if (pageSize) setAutoPrecheckHistoryPageSize(pageSize);
                      }}
                    />
                  </div>
                </div>
              ) : null}
              {autoPrecheckSummary.auditIssues.length > 0 ? (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>{`运单审核失败 ${autoPrecheckSummary.auditIssues.length}条`}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '180px 200px 1fr', border: '1px solid #ffe58f', background: '#fffbe6' }}>
                    <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', fontWeight: 600 }}>指令编号</div>
                    <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', fontWeight: 600 }}>文件</div>
                    <div style={{ padding: '6px 8px', fontWeight: 600 }}>问题</div>
                    {autoPrecheckSummary.auditIssues
                      .slice((autoPrecheckAuditPage - 1) * autoPrecheckAuditPageSize, autoPrecheckAuditPage * autoPrecheckAuditPageSize)
                      .map((it, idx) => (
                      <React.Fragment key={`${it.serial}-${it.fileName}-${idx}`}>
                        <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', borderTop: '1px solid #ffe58f' }}>{it.serial || '-'}</div>
                        <div style={{ padding: '6px 8px', borderRight: '1px solid #ffe58f', borderTop: '1px solid #ffe58f' }}>{it.fileName || '-'}</div>
                        <div style={{ padding: '6px 8px', borderTop: '1px solid #ffe58f' }}>{`${it.label || ''}${it.label ? ':' : ''}${it.message || ''}` || '-'}</div>
                      </React.Fragment>
                    ))}
                  </div>
                  <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                    <Pagination
                      size="small"
                      current={autoPrecheckAuditPage}
                      pageSize={autoPrecheckAuditPageSize}
                      total={autoPrecheckSummary.auditIssues.length}
                      showSizeChanger
                      pageSizeOptions={[5, 10, 20, 50] as any}
                      onChange={(page, pageSize) => {
                        setAutoPrecheckAuditPage(page);
                        if (pageSize) setAutoPrecheckAuditPageSize(pageSize);
                      }}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div style={{ padding: '8px 0' }}>未发现异常。</div>
          ))}
          {autoPrecheckRunning ? (
            <div style={{ marginTop: 12 }}>
              <Spin />
            </div>
          ) : null}
        </Modal>
      </Card>
    </div>
  );
};

export default InstructionBatchListPage;
